// MinhaLoja/WhatsApp/WhatsAppChat.js

import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import EmojiPicker from 'emoji-picker-react';
import DOMPurify from 'dompurify';

import {
    Send, Search, CheckCheck, MessageSquare, Plus, X, Zap,
    Edit3, Trash2, User, Paperclip, Download, Filter, Camera,
    FileText, ExternalLink, ShoppingBag, Users, Smile, ChevronLeft, Mic, Menu, Lock, Unlock, Megaphone, CornerUpLeft, ChevronDown, Copy,
    PanelRight, Tag, Target, CheckCircle, XCircle, Pin, PinOff, Smartphone, ArrowRightLeft, UserPlus, List, Check,Clock
} from 'lucide-react';

import '../styles/WhatsAppChat.css';
import { useAudioRecorder } from "../hooks/useAudioRecorder";

const BASE_URL = 'https://app.universomidiadigital.com.br';

const WhatsAppIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
    </svg>
);

const WebchatIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
    </svg>
);

const cleanJid = (jid) => {
    if (!jid) return '';
    let raw = String(jid);
    if (raw.startsWith('web_')) return raw.split('@')[0];

    let prefix = raw.split('@')[0];
    let domain = raw.split('@')[1] || '';

    prefix = prefix.replace(/^\++/, '');

    if (domain === 's.whatsapp.net' || domain === 'c.us' || !domain) {
        let nums = prefix.replace(/\D/g, '');
        if (nums.startsWith('55') && nums.length === 13) {
            nums = nums.slice(0, 4) + nums.slice(5);
        }
        return nums;
    }
    return prefix;
};

const getWebchatPresenceKey = (jid) => {
    if (!jid) return '';
    let value = String(jid).trim();
    if (value.startsWith('web_')) {
        value = value.split('@')[0];
    }
    return value;
};

const getSafeUserName = () => {
    try {
        const chaves = ['user', 'usuario', 'userData', 'auth', 'user-info'];
        for (let k of chaves) {
            const data = localStorage.getItem(k);
            if (data) {
                const parsed = JSON.parse(data);
                if (parsed?.nome) return parsed.nome;
                if (parsed?.name) return parsed.name;
                if (parsed?.usuario?.nome) return parsed.usuario.nome;
            }
        }
    } catch(e) {}
    return localStorage.getItem('userName') || localStorage.getItem('nome_usuario') || "Sistema";
};

const getSafeUserId = () => {
    try {
        const chaves = ['user', 'usuario', 'userData', 'auth', 'user-info'];
        for (let k of chaves) {
            const data = localStorage.getItem(k);
            if (data) {
                const parsed = JSON.parse(data);
                if (parsed?.id) return parsed.id;
                if (parsed?.usuario?.id) return parsed.usuario.id;
            }
        }
    } catch(e) {}
    return null;
};

// =========================================================================
// RENDERIZAÇÃO BLINDADA DE STATUS (RELÓGIO / 1 TIQUE / 2 TIQUES / VISTO AZUL)
// =========================================================================
// =========================================================================
// RENDERIZAÇÃO DE STATUS PADRÃO IFOOD / WHATSAPP
// =========================================================================
const renderMessageStatus = (status, size = 14) => {
    if (status === null || status === undefined || status === '') {
        return <CheckCheck size={size} color="#8696a0" strokeWidth={2} />;
    }

    const valStr = String(status).trim().toUpperCase();
    const valNum = Number(status);

    // 1. VISUALIZADA / LIDA (2 TIQUES AZUIS)
    if (
        valStr === 'READ' ||
        valStr === 'PLAYED' ||
        valStr === 'VIEWED' ||
        valStr === '4' ||
        valStr === '5' ||
        valNum === 4 ||
        valNum === 5
    ) {
        return (
            <CheckCheck
                size={size}
                color="#53bdeb"
                strokeWidth={2}
            />
        );
    }

    // 2. ENTREGUE NO APARELHO / CONFIRMADA (2 TIQUES CINZAS - PADRÃO IFOOD)
    if (
        valStr === 'DELIVERED' ||
        valStr === 'DELIVERY_ACK' ||
        valStr === 'RECEIVED' ||
        valStr === 'SENT' ||
        valStr === 'SERVER_ACK' ||
        valStr === '2' ||
        valStr === '3' ||
        valNum === 2 ||
        valNum === 3
    ) {
        return (
            <CheckCheck
                size={size}
                color="#8696a0"
                strokeWidth={2}
            />
        );
    }

    // 3. PENDENTE / PROCESSANDO ENVIO (RELÓGIO)
    if (
        valStr === 'PENDING' ||
        valStr === '0' ||
        valStr === '1' ||
        valNum === 0 ||
        valNum === 1
    ) {
        return (
            <Clock
                size={size}
                color="#8696a0"
                strokeWidth={2}
            />
        );
    }

    // Padrão: 2 tiques cinzas
    return (
        <CheckCheck
            size={size}
            color="#8696a0"
            strokeWidth={2}
        />
    );
};


const Events = ({ isOpen, onClose, estabelecimentoId, remoteJid, nomeOperador, idOperador }) => {
    const [nomeEvento, setNomeEvento] = useState('');
    const [tipoEvento, setTipoEvento] = useState('neutral');
    const [descricao, setDescricao] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!remoteJid) return toast.error("Nenhum chat selecionado.");
        if (!nomeEvento) return toast.error("Preencha o nome do evento.");

        setLoading(true);
        try {
            const payload = {
                remote_jid: remoteJid, 
                nome_evento: nomeEvento,
                tipo_evento: tipoEvento,
                descricao: descricao,
                operador: getSafeUserName() || 'Sistema', 
                usuario_id: getSafeUserId() || null 
            };

            await api.post(`/whatsapp/events/${estabelecimentoId}`, payload);
            toast.success("Evento aplicado com sucesso!");

            setNomeEvento('');
            setDescricao('');
            setTipoEvento('neutral');
            onClose();

        } catch (err) {
            toast.error("Erro ao aplicar evento.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="wa-overlay" style={{ zIndex: 999999 }}>
            <div className="wa-modal-premium" style={{ maxWidth: '450px', width: '90%' }}>
                <div className="wa-modal-premium-header" style={{ background: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Target size={20} color="#00a884" />
                        <h3 style={{ margin: 0, color: '#111b21' }}>Aplicar Evento</h3>
                    </div>
                    <X size={24} onClick={onClose} style={{ cursor: 'pointer', color: '#667781' }} />
                </div>

                <form onSubmit={handleSubmit} className="wa-modal-premium-body">
                    <div className="wa-input-group-premium" style={{ marginBottom: '15px' }}>
                        <label>Nome do Evento</label>
                        <input 
                            autoFocus
                            value={nomeEvento} 
                            onChange={e => setNomeEvento(e.target.value)} 
                            required 
                            placeholder="Ex: Atendimento Encerrado, Carrinho Abandonado..." 
                        />
                    </div>

                    <div className="wa-input-group-premium" style={{ marginBottom: '15px' }}>
                        <label>Tipo de Evento</label>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                            <button type="button" onClick={() => setTipoEvento('positive')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '8px', border: `1px solid ${tipoEvento === 'positive' ? '#25d366' : '#e1e9eb'}`, background: tipoEvento === 'positive' ? '#eafbf0' : '#fff', borderRadius: '8px', cursor: 'pointer', color: '#111b21', fontWeight: tipoEvento === 'positive' ? '600' : '400' }}>
                                <CheckCircle size={16} color={tipoEvento === 'positive' ? "#25d366" : "#667781"} /> Positivo
                            </button>
                            <button type="button" onClick={() => setTipoEvento('negative')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '8px', border: `1px solid ${tipoEvento === 'negative' ? '#ea1d2c' : '#e1e9eb'}`, background: tipoEvento === 'negative' ? '#fdedee' : '#fff', borderRadius: '8px', cursor: 'pointer', color: '#111b21', fontWeight: tipoEvento === 'negative' ? '600' : '400' }}>
                                <XCircle size={16} color={tipoEvento === 'negative' ? "#ea1d2c" : "#667781"} /> Negativo
                            </button>
                            <button type="button" onClick={() => setTipoEvento('neutral')} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '8px', border: `1px solid ${tipoEvento === 'neutral' ? '#8a9fac' : '#e1e9eb'}`, background: tipoEvento === 'neutral' ? '#f0f2f5' : '#fff', borderRadius: '8px', cursor: 'pointer', color: '#111b21', fontWeight: tipoEvento === 'neutral' ? '600' : '400' }}>
                                <Target size={16} color={tipoEvento === 'neutral' ? "#8a9fac" : "#667781"} /> Neutro
                            </button>
                        </div>
                    </div>

                    <div className="wa-input-group-premium" style={{ marginBottom: '15px' }}>
                        <label>Descrição Opcional</label>
                        <textarea 
                            rows="3"
                            value={descricao} 
                            onChange={e => setDescricao(e.target.value)} 
                            placeholder="Ex: Fidelizado por busca do próximo na fila..." 
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e1e9eb', resize: 'none', fontFamily: 'inherit' }}
                        />
                    </div>
                </form>

                <div className="wa-modal-footer-premium">
                    <button type="button" className="wa-btn-cancel" onClick={onClose} disabled={loading}>Cancelar</button>
                    <button type="button" className="wa-btn-save" onClick={handleSubmit} disabled={loading}>
                        {loading ? 'Aplicando...' : 'Aplicar Evento'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const WhatsAppChat = ({ estabelecimentoId }) => {
    const { isRecording, recordingTime, startRecording, stopRecording } = useAudioRecorder((audioBlob) => {
        handleSendAudio(audioBlob);
    });

    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
    const [conversas, setConversas] = useState([]);
    const [activeTab, setActiveTab] = useState('whatsapp'); 
    const [mensagens, setMensagens] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [newMessage, setNewMessage] = useState("");
    const [replyingTo, setReplyingTo] = useState(null);
    const [activeMessageMenu, setActiveMessageMenu] = useState(null);
    const [mentionHover, setMentionHover] = useState(null);

    const [onlineWebchats, setOnlineWebchats] = useState(new Set());

    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [selectedTagFilter, setSelectedTagFilter] = useState(null);

    const [showActionMenu, setShowActionMenu] = useState(false); 
    const [showTemplates, setShowTemplates] = useState(false);
    const [showTagManager, setShowTagManager] = useState(false); 
    const [showEventModal, setShowEventModal] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const [showNewChatModal, setShowNewChatModal] = useState(false);
    const [newChatTab, setNewChatTab] = useState('lista'); 
    const [newChatNumber, setNewChatNumber] = useState('');
    const [globalContacts, setGlobalContacts] = useState([]);
    const [searchContatoModal, setSearchContatoModal] = useState('');
    const [loadingContatos, setLoadingContatos] = useState(false);
    const [contatoSelecionadoModal, setContatoSelecionadoModal] = useState(null);

    const [fabPos, setFabPos] = useState(null); 
    const isDraggingFab = useRef(false);

    const [rightPanelOpen, setRightPanelOpen] = useState(false); 
    const [showInstanceMenu, setShowInstanceMenu] = useState(false); 

    const [templates, setTemplates] = useState([]);
    const [contactExists, setContactExists] = useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [newTagData, setNewTagData] = useState({ nome: '', cor: '#00a884', descricao: '' });
    const [editingTag, setEditingTag] = useState(null);
    const [editTagData, setEditTagData] = useState({ nome: '', cor: '#00a884', descricao: '' });
    const [motivoTag, setMotivoTag] = useState(''); 

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, chat: null });
    const [showAddContactModal, setShowAddContactModal] = useState(false);
    const [loadingCep, setLoadingCep] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [fileToUpload, setFileToUpload] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const [fileCaption, setFileCaption] = useState("");
    const [exibirNomeOperador, setExibirNomeOperador] = useState(false);
    const [permitirUpload, setPermitirUpload] = useState(true); 

    const [webchatSettings, setWebchatSettings] = useState(null); 
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

    const [customLabels, setCustomLabels] = useState(() => {
        const saved = localStorage.getItem(`customLabels_${estabelecimentoId}`);
        return saved ? JSON.parse(saved) : { coringa1: 'Campo Extra 1', coringa2: 'Campo Extra 2', coringa3: 'Campo Extra 3' };
    });

    const [instances, setInstances] = useState([]);
    const [selectedInstanceId, setSelectedInstanceId] = useState(null);

    const isWebchatEnabled = webchatSettings ? webchatSettings.enableWebchat !== false : true;

    const [nomeOperador, setNomeOperador] = useState(getSafeUserName());
    const [idOperador, setIdOperador] = useState(getSafeUserId());
    const [currentChannel, setCurrentChannel] = useState('whatsapp'); 

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setNomeOperador(getSafeUserName());
        setIdOperador(getSafeUserId());
    }, []);

    useEffect(() => {
        if (!showNewChatModal || newChatTab !== 'lista') {
            setContatoSelecionadoModal(null);
        }
    }, [showNewChatModal, newChatTab]);

    const [contactData, setContactData] = useState({
        id: null, nome: '', email: '', cpf: '', whatsapp: '',
        cep: '', endereco: '', numero: '', complemento: '',
        bairro: '', cidade: '', estado: '',
        lat_cliente: null, lng_cliente: null,
        coringa1: '', coringa2: '', coringa3: '',
        remoteJidOriginal: '' 
    });

    const selectedChatRef = useRef(null);
    const socketRef = useRef(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null); 
    const isDraggingRef = useRef(false);
    const notifySoundRef = useRef(null);
    const textareaRef = useRef(null);

    const isWebchatOnline = (chat) => {
        if (!chat) return false;
        const key = getWebchatPresenceKey(chat.remote_jid || chat.remoteJidOriginal || chat.whatsapp);
        if (!key) return false;
        return onlineWebchats.has(key);
    };

    const handleFabTouchMove = (e) => {
        isDraggingFab.current = true;
        const touch = e.touches[0];
        setFabPos({ x: touch.clientX - 28, y: touch.clientY - 28 });
    };

    const handleFabTouchEnd = (e) => {
        setTimeout(() => { isDraggingFab.current = false; }, 100);
    };

    const carregarTodosContatos = async () => {
        setLoadingContatos(true);
        try {
            const res = await api.get(`/whatsapp/contacts/${estabelecimentoId}`);
            if (res.data?.payload) {
                setGlobalContacts(res.data.payload);
            } else if (Array.isArray(res.data)) {
                setGlobalContacts(res.data);
            }
        } catch (err) {
            console.error("Erro ao buscar contatos globais", err);
        } finally {
            setLoadingContatos(false);
        }
    };

    useEffect(() => {
        carregarTodosContatos();
    }, [estabelecimentoId]);

    const resolveContactName = (phoneRaw) => {
        if (!phoneRaw) return null;
        const num = String(phoneRaw).replace(/\D/g, '');
        if (!num) return null;

        const contact = globalContacts.find(c => {
            const cNum = String(c.whatsapp || c.telefone || c.remote_jid_original || '').replace(/\D/g, '');
            return cNum && num && cNum.length >= 8 && num.length >= 8 && cNum.slice(-8) === num.slice(-8);
        });
        return contact ? contact.nome : null;
    };

    const iniciarNovaConversa = (numeroBruto, nomeOp, picUrl) => {
        let num = String(numeroBruto).replace(/\D/g, '');
        if (!num) return toast.error("Número inválido.");
        if (!num.startsWith('55') && num.length <= 11) num = '55' + num;

        const newJid = `${num}@s.whatsapp.net`;
        let existing = conversas.find(c => cleanJid(c.remote_jid) === cleanJid(newJid));

        if (existing) {
            carregarHistorico(existing);
        } else {
            const newChat = {
                remote_jid: newJid,
                contactName: nomeOp || num,
                profilePicUrl: picUrl || null,
                lastMsg: '',
                type: 'conversation',
                timestamp: Date.now(),
                from_me: 1,
                tags: [],
                isWebChat: false,
                is_favorite: false,
                unreadCount: 0
            };
            setConversas(prev => [newChat, ...prev]);
            carregarHistorico(newChat);
        }
        setShowNewChatModal(false);
        setNewChatNumber('');
    };

    const handleNewChatSubmitAvulso = (e) => {
        e.preventDefault();
        iniciarNovaConversa(newChatNumber, null, null);
    };

    const handleIniciarChatPorContato = (contato) => {
        const numero = contato.whatsapp || contato.telefone || contato.remote_jid_original;
        if (!numero) return toast.error("Este contato não possui telefone cadastrado.");
        iniciarNovaConversa(numero, contato.nome, contato.profile_pic_url);
    };

    const isInvalidContact = (contact) => {
        const checkFields = [
            String(contact.nome || '').toLowerCase().trim(),
            String(contact.whatsapp || '').toLowerCase().trim(),
            String(contact.remote_jid_original || '').toLowerCase().trim(),
            String(contact.telefone || '').toLowerCase().trim()
        ];

        return checkFields.some(field => 
            field.includes('@newsletter') || 
            field.includes('@g.us') || 
            field.includes('@g') || 
            field.includes('@broadcast') || 
            field.startsWith('1203') || 
            field.includes('120363') || 
            field.endsWith('@n')
        );
    };

    const contatosLimposDb = [];
    const chavesProcessadas = new Set();

    globalContacts.forEach(c => {
        if (isInvalidContact(c)) return;

        let numLimpo = String(c.whatsapp || c.remote_jid_original || c.telefone || '').replace(/\D/g, '');
        if (numLimpo.startsWith('55') && numLimpo.length >= 12) numLimpo = numLimpo.substring(2);

        let chaveIdentificadora = '';
        if (numLimpo.length >= 8) {
            chaveIdentificadora = numLimpo.slice(-8);
        } else {
            chaveIdentificadora = String(c.nome || '').toLowerCase().trim();
        }

        if (chaveIdentificadora && !chavesProcessadas.has(chaveIdentificadora)) {
            chavesProcessadas.add(chaveIdentificadora);
            contatosLimposDb.push(c);
        }
    });

    const contatosFiltradosModal = contatosLimposDb.filter(c => {
        const termo = searchContatoModal.toLowerCase();
        if (!termo) return true;

        const nome = String(c.nome || '').toLowerCase();
        const num = String(c.whatsapp || c.telefone || c.remote_jid_original || '').toLowerCase();

        return nome.includes(termo) || num.includes(termo);
    });

    const handleEditarDaAgenda = (contato) => {
        setContactData({
            id: contato.id, nome: contato.nome || '', email: contato.email || '', cpf: contato.cpf || '',
            whatsapp: contato.whatsapp || contato.telefone || contato.remote_jid_original || '', cep: contato.cep || '',
            endereco: contato.endereco || '', numero: contato.numero || '', complemento: contato.complemento || '',
            bairro: contato.bairro || '', cidade: contato.cidade || '', estado: contato.estado || '', coringa1: contato.coringa1 || '',
            coringa2: contato.coringa2 || '', coringa3: contato.coringa3 || '', remoteJidOriginal: contato.remote_jid_original || contato.whatsapp 
        });
        setShowAddContactModal(true);
    };

    const handleExcluirContatoAgenda = (contato) => {
        const idContato = contato.id;
        const numParaExcluir = String(contato.whatsapp || contato.remote_jid_original || contato.telefone || '').replace(/\D/g, '');

        setConfirmModal({
            show: true, 
            title: 'Excluir Contato Definitivamente', 
            message: `Tem certeza que deseja apagar o registro de ${contato.nome || 'este contato'} do banco de dados?`,
            onConfirm: async () => {
                try {
                    await api.delete(`/whatsapp/contacts/${estabelecimentoId}/${idContato}`, { data: { whatsapp: numParaExcluir } });
                    setGlobalContacts(prev => prev.filter(c => c.id !== idContato));
                    setContatoSelecionadoModal(null);
                    toast.success("Contato excluído do banco de dados!");
                    carregarTodosContatos();
                    if (selectedChat) carregarHistorico(selectedChat);
                } catch (err) {
                    try {
                        await api.post(`/whatsapp/remove-contact/${estabelecimentoId}`, { id: idContato, whatsapp: numParaExcluir });
                        setGlobalContacts(prev => prev.filter(c => c.id !== idContato));
                        setContatoSelecionadoModal(null);
                        toast.success("Contato excluído do banco de dados!");
                        carregarTodosContatos();
                        if (selectedChat) carregarHistorico(selectedChat);
                    } catch (e) {
                        toast.error("Erro ao excluir contato.");
                    }
                }
            }
        });
    };

    const carregarInstancias = async () => {
        try {
            const res = await api.get(`/whatsapp/instances/${estabelecimentoId}`);
            if (res.data?.payload?.length > 0) {
                const connectedInstances = res.data.payload.filter(i => i.status === 'conectado');
                setInstances(connectedInstances);

                if (connectedInstances.length > 0) {
                    setSelectedInstanceId(prev => {
                        if (!prev || !connectedInstances.find(i => i.id === prev)) {
                            return connectedInstances[0].id;
                        }
                        return prev;
                    });
                }
            } else {
                setInstances([]);
                setSelectedInstanceId(null);
            }
        } catch (err) {
            console.error("Erro ao buscar instâncias:", err);
        }
    };

    const chatInstanceId = selectedChat?.instance_id || selectedInstanceId;

    const currentInstance = instances.find(
        i => Number(i.id) === Number(chatInstanceId)
    );

    const isSelectedChatGroup =
        String(selectedChat?.remote_jid || '').endsWith('@g.us') ||
        selectedChat?.chat_type === 'group';

    const isSelectedChatNewsletter =
        String(selectedChat?.remote_jid || '').endsWith('@newsletter') ||
        selectedChat?.chat_type === 'newsletter';

    const showChatLineBadge =
        selectedChat &&
        currentChannel === 'whatsapp' &&
        (isSelectedChatGroup || isSelectedChatNewsletter);

    const handleInstanceChange = async (newId) => {
        if (isSelectedChatGroup) {
            setShowInstanceMenu(false);
            toast.error("A linha de um grupo não pode ser alterada.");
            return;
        }

        const numId = Number(newId);
        const oldInstance = instances.find(i => Number(i.id) === Number(selectedInstanceId));
        const newInstance = instances.find(i => Number(i.id) === numId);

        if (!newInstance) {
            toast.error("Linha selecionada não encontrada.");
            return;
        }

        setShowInstanceMenu(false);

        if (oldInstance && Number(oldInstance.id) === Number(newInstance.id)) return;

        setSelectedInstanceId(numId);
        if (!selectedChat?.remote_jid) return;
        if (!oldInstance) return;

        try {
            await api.post(
                `/whatsapp/events/${estabelecimentoId}`,
                {
                    remote_jid: selectedChat.remote_jid,
                    nome_evento: 'Troca de Linha',
                    tipo_evento: 'line_swap',
                    descricao: `Linha alterada de [${oldInstance.name}] para [${newInstance.name}]`,
                    operador: getSafeUserName() || 'Sistema',
                    usuario_id: getSafeUserId() || null
                }
            );
        } catch (err) {
            console.error("Erro ao registrar evento de troca de linha:", err);
        }
    };

    useEffect(() => {
        notifySoundRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
        const carregarConfiguracoes = async () => {
            try {
                const res = await api.get(`/whatsapp/settings/${estabelecimentoId}`);
                if (res.data && res.data.payload) {
                    setWebchatSettings(res.data.payload);
                    setPermitirUpload(res.data.payload.enableUpload !== 0);
                }
            } catch (err) {}
        };
        carregarConfiguracoes();
        carregarTags();
        carregarInstancias();
    }, [estabelecimentoId]);

    useEffect(() => { selectedChatRef.current = selectedChat; }, [selectedChat]);

    const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); };
    useEffect(() => { scrollToBottom(); }, [mensagens]);

    useEffect(() => {
        if (!selectedChat || !socketRef.current) return;

        const remoteJid = String(selectedChat.remote_jid || '').trim();
        const isWeb = selectedChat.isWebChat === true ||
                      selectedChat.channel === 'webchat' ||
                      selectedChat.chat_type === 'webchat' ||
                      remoteJid.startsWith('web_');

        if (!isWeb) return;

        const webchatId = getWebchatPresenceKey(remoteJid);
        if (!webchatId) return;

        const consultarStatus = () => {
            if (socketRef.current?.connected) {
                socketRef.current.emit('webchat-check-status', {
                    webchatId,
                    remote_jid: webchatId,
                    estabelecimentoId
                });
            }
        };

        consultarStatus();
        const timer = setTimeout(consultarStatus, 1000);
        return () => clearTimeout(timer);

    }, [selectedChat?.remote_jid, estabelecimentoId]);

    const handleMentionHover = (e) => {
        const target = e.target.closest('.mention-tag');
        if (target) {
            const phoneOrName = target.getAttribute('data-mention');
            if (!phoneOrName) return;

            const isMentionNumberOnly = /^\d+$/.test(String(phoneOrName).trim());
            const numClean = String(phoneOrName).replace(/\D/g, '');

            let contact = globalContacts.find(c => {
                const cNum = String(c.whatsapp || c.telefone || c.remote_jid_original || '').replace(/\D/g, '');
                if (isMentionNumberOnly && numClean && cNum && cNum.length >= 8 && numClean.length >= 8 && cNum.slice(-8) === numClean.slice(-8)) return true;
                if (String(c.nome).toLowerCase().trim() === String(phoneOrName).toLowerCase().trim()) return true;
                return false;
            });

            const rect = target.getBoundingClientRect();
            setMentionHover({
                x: rect.left + (rect.width / 2),
                y: rect.top - 8,
                phone: phoneOrName,
                contact: contact || { 
                    nome: isMentionNumberOnly ? 'Não salvo na agenda' : phoneOrName, 
                    whatsapp: isMentionNumberOnly ? phoneOrName : 'Sem número' 
                }
            });
        }
    };

    const handleMentionLeave = (e) => {
        if (e.target.closest('.mention-tag')) {
            setMentionHover(null);
        }
    };

    const handleMentionClick = (e) => {
        const target = e.target.closest('.mention-tag');
        if (target) {
            e.stopPropagation();
            const mentionValue = target.getAttribute('data-mention');
            if (!mentionValue) return;

            const isMentionNumberOnly = /^\d+$/.test(String(mentionValue).trim());
            const numClean = String(mentionValue).replace(/\D/g, '');

            let contact = globalContacts.find(c => {
                const cNum = String(c.whatsapp || c.telefone || c.remote_jid_original || '').replace(/\D/g, '');
                if (isMentionNumberOnly && numClean && cNum && cNum.length >= 8 && numClean.length >= 8 && cNum.slice(-8) === numClean.slice(-8)) return true;
                if (String(c.nome).toLowerCase().trim() === String(mentionValue).toLowerCase().trim()) return true;
                return false;
            });

            if (contact) {
                handleEditarDaAgenda(contact);
            } else {
                if (isMentionNumberOnly && numClean) {
                    setContactData({
                        id: null, nome: '', email: '', cpf: '', whatsapp: numClean,
                        cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', coringa1: '', coringa2: '', coringa3: '',
                        remoteJidOriginal: numClean
                    });
                    setShowAddContactModal(true);
                } else {
                    toast.error("Contato não encontrado na base de dados.");
                }
            }
        }
    };

    const scrollToQuotedMessage = (msgData, currentIndex) => {
        const qText = msgData.quoted_content;
        if (!qText) return;

        const foundMsg = [...mensagens].slice(0, currentIndex).reverse().find(m => {
            const txt = m.text || m.content || m.dbText || "";
            return txt.includes(qText) || qText.includes(txt); 
        });

        const targetId = msgData.quoted_id || (foundMsg ? foundMsg.msg_id : null);

        if (targetId) {
            const el = document.getElementById(`msg-${targetId}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const originalBg = el.style.backgroundColor;
                el.style.backgroundColor = 'rgba(0, 168, 132, 0.3)';
                setTimeout(() => {
                    el.style.backgroundColor = originalBg;
                }, 1500);
            } else {
                toast('A mensagem original é muito antiga para ser exibida.');
            }
        } else {
            toast('Mensagem não encontrada no histórico visível.');
        }
    };

    const onEmojiClick = (emojiData) => {
        setNewMessage(prev => prev + emojiData.emoji);
    };

    const handleReact = async (msg, emoji) => {
        try {
            if (!msg || !selectedChat) {
                toast.error("Mensagem ou conversa inválida.");
                return;
            }

            const msgId = msg.msg_id || msg.key?.id || msg.id;

            if (!msgId) {
                console.error("Mensagem sem ID para reação:", msg);
                toast.error("Não foi possível identificar a mensagem.");
                return;
            }

            const remoteJid = msg.remote_jid || msg.remoteJid || msg.key?.remoteJid || selectedChat.remote_jid;

            let participant = msg.key?.participant || msg.original_participant || msg.participant || msg.participant_jid || msg.sender_jid || msg.author || msg.quoted_participant || null;

            if (!participant && msg.participant_phone) {
                const raw = String(msg.participant_phone).trim();
                if (raw.includes('@')) {
                    participant = raw;
                } else {
                    const nums = raw.replace(/\D/g, '');
                    if (nums) participant = `${nums}@s.whatsapp.net`;
                }
            }

            const fromMe = msg.from_me === 1 || msg.from_me === true || msg.fromMe === true || msg.key?.fromMe === true;

            const reactionInstanceId =
                msg.instance_id ||
                msg.instanceId ||
                selectedChat?.instance_id ||
                selectedInstanceId ||
                null;

            const response = await api.post(
                `/whatsapp/react/${estabelecimentoId}`,
                {
                    msgId,
                    emoji,
                    remoteJid,
                    participant,
                    fromMe,
                    instanceId: reactionInstanceId
                }
            );

            if (response.data?.success === false) throw new Error(response.data?.error || "Erro ao enviar reação");

            setMensagens(prev => prev.map(m => {
                const currentId = m.msg_id || m.key?.id || m.id;
                return currentId === msgId ? { ...m, reaction: emoji } : m;
            }));

            setActiveMessageMenu(null);
        } catch (err) {
            console.error("Erro ao enviar reação:", err.response?.data || err);
            toast.error(err.response?.data?.error || "Erro ao enviar reação.");
        }
    };

    const handleStartRecord = (e) => {
        if (e.cancelable) e.preventDefault();
        isDraggingRef.current = true;
        startRecording(); 
    };

    const handleStopRecord = (e) => {
        if (e.cancelable) e.preventDefault();
        isDraggingRef.current = false;
        if (isRecording) stopRecording();
    };

    const handleSendAudio = async (audioBlob) => {
        if (!selectedChat) return;

        let targetJid = selectedChat.remote_jid;
        if (currentChannel === 'webchat') {
            if (!String(targetJid).startsWith('web_') && contactData?.remoteJidOriginal?.startsWith('web_')) {
                targetJid = contactData.remoteJidOriginal;
            }
        } else if (currentChannel === 'whatsapp') {
            if (String(targetJid).startsWith('web_')) {
                if (contactData?.whatsapp) {
                    const numLimpo = contactData.whatsapp.replace(/\D/g, '');
                    targetJid = numLimpo.includes('@') ? numLimpo : `${numLimpo}@s.whatsapp.net`;
                } else {
                    return toast.error("Este visitante não possui WhatsApp cadastrado para transição!");
                }
            }
        }

        const tempId = `temp_audio_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
        const file = new File([audioBlob], `voice_${Date.now()}.ogg`, { type: "audio/ogg" });

        const formData = new FormData();
        formData.append('number', targetJid);
        formData.append('channel', currentChannel);
        formData.append('media', file);
        formData.append('requestId', tempId); 
        formData.append('pushName', contactData?.nome || selectedChat?.contactName || '');

        if (selectedInstanceId && currentChannel === 'whatsapp') {
            formData.append('instanceId', selectedInstanceId);
        }

        if (replyingTo) {
            formData.append('replyTo', replyingTo.msg_id || replyingTo.key?.id);
        }

        try {
            await api.post(`/whatsapp/send/${estabelecimentoId}`, formData);
            setReplyingTo(null);
        } catch (err) { toast.error("Erro ao enviar áudio."); }
    };

    const processarVariaveis = (texto) => {
        if (!texto) return "";
        const nomeCliente = contactData?.nome || selectedChat?.contactName || "Cliente";
        const primeiroNome = nomeCliente.split(' ')[0];
        const horaAtual = new Date().getHours();
        let saudacao = 'Boa noite';
        if (horaAtual >= 5 && horaAtual < 12) saudacao = 'Bom dia';
        else if (horaAtual >= 12 && horaAtual < 18) saudacao = 'Boa tarde';

        return texto
            .replace(/@NomeContato/gi, nomeCliente)
            .replace(/@Nome/gi, primeiroNome)
            .replace(/@Saudacao/gi, saudacao)
            .replace(/@EmailContato/gi, contactData?.email || "")
            .replace(/@TelefoneContato/gi, contactData?.whatsapp || "")
            .replace(/@v1/gi, contactData?.coringa1 || "")
            .replace(/@v2/gi, contactData?.coringa2 || "")
            .replace(/@v3/gi, contactData?.coringa3 || "");
    };

    const handleSelectTemplate = (template) => {
        const textoOriginal = template.Descricao || "";
        const textoProcessado = processarVariaveis(textoOriginal);
        setNewMessage(prev => prev ? `${prev}\n${textoProcessado}` : textoProcessado);
        setShowTemplates(false);
        textareaRef.current?.focus();
    };

    const handleSendTemplateDirect = async (template) => {
        if (!selectedChat) return;

        let targetJid = selectedChat.remote_jid;
        if (currentChannel === 'webchat') {
            if (!String(targetJid).startsWith('web_') && contactData?.remoteJidOriginal?.startsWith('web_')) targetJid = contactData.remoteJidOriginal;
        } else if (currentChannel === 'whatsapp') {
            if (String(targetJid).startsWith('web_')) {
                if (contactData?.whatsapp) {
                    const numLimpo = contactData.whatsapp.replace(/\D/g, '');
                    targetJid = numLimpo.includes('@') ? numLimpo : `${numLimpo}@s.whatsapp.net`;
                } else {
                    return toast.error("Visitante sem WhatsApp cadastrado!");
                }
            }
        }

        const prefixo = exibirNomeOperador ? `*[${nomeOperador}]*\n` : "";
        const textoProcessado = prefixo + processarVariaveis(template.Descricao || "");
        const tempId = `temp_tpl_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`; 

        let mediaUrlPreview = null;
        if (template.CaminhoImagem) {
            mediaUrlPreview = template.CaminhoImagem.startsWith('http') ? template.CaminhoImagem : `${BASE_URL}${template.CaminhoImagem}`;
        } else if (template.UrlVideo) {
            mediaUrlPreview = template.UrlVideo;
        }

        const localMsg = {
            msg_id: tempId, remote_jid: targetJid, text: textoProcessado, from_me: 1, timestamp: Date.now(),
            type: template.TipoArquivo === 'video' ? 'video' : (template.TipoArquivo === 'imagem' ? 'image' : 'conversation'),
            isWebChat: currentChannel === 'webchat', mediaUrl: mediaUrlPreview, status: 'SENT', statusCode: 2
        };

        setMensagens(prev => [...prev, localMsg]);
        setShowTemplates(false);

        try {
            const formData = new FormData();
            formData.append('number', targetJid);
            formData.append('templateId', template.id); 
            formData.append('text', textoProcessado); 
            formData.append('channel', currentChannel);
            formData.append('requestId', tempId); 
            formData.append('pushName', contactData?.nome || selectedChat?.contactName || '');

            if (selectedInstanceId && currentChannel === 'whatsapp') {
                formData.append('instanceId', selectedInstanceId);
            }

            const response = await api.post(`/whatsapp/send/${estabelecimentoId}`, formData);
            const newMsgId = response?.data?.payload?.msg_id || response?.data?.msg_id;

            if (newMsgId) {
                setMensagens(prev => prev.map(m => m.msg_id === tempId ? { ...m, msg_id: newMsgId, status: 'SENT', statusCode: 2 } : m));
            }

            setConversas(prev => {
                const map = new Map();
                prev.forEach(c => map.set(cleanJid(c.remote_jid), c));
                const targetClean = cleanJid(selectedChat.remote_jid);
                if (map.has(targetClean)) {
                    map.set(targetClean, { ...map.get(targetClean), lastMsg: textoProcessado || "Mídia", timestamp: Date.now(), from_me: 1, unreadCount: 0 });
                }
                return Array.from(map.values()).sort((a, b) => b.timestamp - a.timestamp);
            });

            toast.success("Template enviado!");
        } catch (err) {
            setMensagens(prev => prev.filter(m => m.msg_id !== tempId));
            toast.error("Falha ao enviar o template.");
        }
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (contactData.id) await handleUpdateContact(e);
        else await handleSaveFullContact(e);
    };

    const handleUpdateContact = async (e) => {
        if (e) e.preventDefault();
        if (!contactData.id) return toast.error("Selecione um contato para editar.");

        try {
            const payload = { ...contactData };
            if (!payload.email || payload.email.trim() === "") payload.email = null;
            if (!payload.cpf || payload.cpf.trim() === "") payload.cpf = null;

            await api.put(`/whatsapp/contact/update/${estabelecimentoId}/${contactData.id}`, payload);
            toast.success("Dados do contato atualizados com sucesso!");
            setShowAddContactModal(false);
            carregarConversas();
            carregarTodosContatos(); 
            if (selectedChat) carregarHistorico(selectedChat);

        } catch (err) { 
            toast.error(err.response?.data?.error || "Erro ao atualizar dados."); 
        }
    };

    const handleSaveFullContact = async (e) => {
        if (e) e.preventDefault();
        try {
            const payload = { 
                ...contactData, 
                remoteJidOriginal: contactData.remoteJidOriginal || selectedChat?.remote_jid || "" 
            };

            if (!payload.email || payload.email.trim() === "") payload.email = null;
            if (!payload.cpf || payload.cpf.trim() === "") payload.cpf = null;

            const res = await api.post(`/whatsapp/add-contact/${estabelecimentoId}`, payload);
            if (res.data.success) {
                const isGroup = selectedChat?.remote_jid?.includes('@g.us') || selectedChat?.chat_type === 'group';
                if (!isGroup) setContactExists(true);

                setShowAddContactModal(false);
                toast.success("Cliente cadastrado com sucesso!");
                carregarConversas();
                carregarTodosContatos(); 
                if (selectedChat) carregarHistorico(selectedChat);
            }
        } catch (err) { 
            toast.error(err.response?.data?.error || "Erro ao salvar contato."); 
        }
    };

    const handleDeleteContact = (chatToDelete) => {
        setConfirmModal({
            show: true, title: 'Excluir Dados do Contato', message: 'Tem certeza que deseja apagar o registro deste contato?',
            onConfirm: async () => {
                const whatsapp = chatToDelete.remote_jid.split('@')[0];
                try {
                    await api.post(`/whatsapp/remove-contact/${estabelecimentoId}`, { whatsapp });
                    carregarConversas();
                    carregarTodosContatos();
                    if (selectedChat?.remote_jid === chatToDelete.remote_jid) { setSelectedChat(null); setMensagens([]); }
                    toast.success("Contato excluído.");
                } catch (err) { toast.error("Erro ao excluir."); }
            }
        });
    };

    const toggleUploadSite = async (checked) => {
        setPermitirUpload(checked);
        try {
            await api.post(`/whatsapp/settings/${estabelecimentoId}`, { enableUpload: checked ? 1 : 0 });
            if (socketRef.current) socketRef.current.emit('toggle-upload-webchat', { estabelecimentoId, allow: checked });
            toast.success(checked ? "Visitantes podem enviar arquivos!" : "Upload pelo site bloqueado!");
        } catch (err) { toast.error("Erro ao alterar permissão."); }
    };

    const handleToggleFavorite = async (chat) => {
        try {
            const newStatus = !chat.is_favorite;
            await api.post(`/whatsapp/favorite/${estabelecimentoId}`, { whatsapp: chat.remote_jid, is_favorite: newStatus });

            setConversas(prev => {
                const updated = prev.map(c => cleanJid(c.remote_jid) === cleanJid(chat.remote_jid) ? { ...c, is_favorite: newStatus } : c);
                return updated.sort((a, b) => {
                    const isFavA = a.is_favorite ? 1 : 0;
                    const isFavB = b.is_favorite ? 1 : 0;
                    if (isFavA === isFavB) return b.timestamp - a.timestamp;
                    return isFavB - isFavA;
                });
            });
            toast.success(newStatus ? "Adicionado aos favoritos!" : "Removido dos favoritos!");
            setContextMenu({ ...contextMenu, show: false });
        } catch (err) { toast.error("Erro ao alterar favorito."); }
    };

    const carregarConversas = async () => {
        try {
            const res = await api.get(`/whatsapp/chats/${estabelecimentoId}`);
            if (res.data && res.data.payload) {
                const dataComUnread = res.data.payload.map(c => {
                    let safeName = c.contactName;
                    if (safeName === 'Atendente' || safeName === 'Sistema') safeName = c.remote_jid.split('@')[0];
                    let pic = c.profilePicUrl === 'no_pic' ? null : c.profilePicUrl;

                    const isWeb = c.isWebChat === true || c.channel === 'webchat' || c.chat_type === 'webchat' || String(c.remote_jid).startsWith('web_');

                    return { 
                        ...c, 
                        contactName: safeName, 
                        profilePicUrl: pic, 
                        unreadCount: c.unreadCount || 0, 
                        isWebChat: isWeb,
                        is_favorite: c.is_favorite === 1 || c.is_favorite === true, 
                        eventType: c.event_type || c.eventType || 'neutral' 
                    };
                });

                const uniqueConversas = new Map();
                dataComUnread.forEach(c => {
                    const key = cleanJid(c.remote_jid);
                    if (!uniqueConversas.has(key) || c.timestamp > uniqueConversas.get(key).timestamp) {
                        uniqueConversas.set(key, c);
                    }
                });

                setConversas(Array.from(uniqueConversas.values()).sort((a, b) => {
                    if (a.is_favorite === b.is_favorite) return b.timestamp - a.timestamp;
                    return (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0);
                }));
            }
        } catch (err) { toast.error("Erro ao carregar conversas"); }
    };

    useEffect(() => {
        socketRef.current = io(BASE_URL, { transports: ['websocket'], reconnection: true });
        socketRef.current.on('connect', () => { socketRef.current.emit('join', estabelecimentoId); });

        socketRef.current.on('webchat-presence-snapshot', (data) => {
            const visitors = Array.isArray(data?.visitors) ? data.visitors : [];
            const normalizedVisitors = visitors.map(jid => getWebchatPresenceKey(jid)).filter(Boolean);
            setOnlineWebchats(new Set(normalizedVisitors));
        });

        socketRef.current.on('webchat-status', (data) => {
            if (!data?.remote_jid && !data?.webchatId) return;
            const jid = getWebchatPresenceKey(data.remote_jid || data.webchatId);
            if (!jid) return;

            setOnlineWebchats(prev => {
                const next = new Set(prev);
                if (data.status === 'online') next.add(jid);
                else if (data.status === 'offline') next.delete(jid);
                return next;
            });
        });

        socketRef.current.on('whatsapp-reaction', (data) => {
            setMensagens(prev => prev.map(m => (m.msg_id === data.msg_id || m.key?.id === data.msg_id) ? { ...m, reaction: data.reaction } : m));
        });

        // 🔥 RECEBIMENTO DE STATUS EM TEMPO REAL ATUALIZANDO OS TIQUES
        socketRef.current.on('whatsapp-msg-status', (data) => {
            const targetId = data.msg_id || data.id || data.key?.id;
            const newStatus = data.status || data.statusCode;
            const newCode = data.statusCode || data.status;

            setMensagens(prev =>
                prev.map(m => {
                    const msgId = m.msg_id || m.key?.id || m.id;
                    if (msgId === targetId || (data.requestId && m.msg_id === data.requestId)) {
                        return {
                            ...m,
                            status: newStatus,
                            statusCode: newCode
                        };
                    }
                    return m;
                })
            );

            if (data.remote_jid) {
                setConversas(prev =>
                    prev.map(chat => {
                        if (cleanJid(chat.remote_jid) === cleanJid(data.remote_jid)) {
                            return {
                                ...chat,
                                status: newStatus,
                                statusCode: newCode
                            };
                        }
                        return chat;
                    })
                );
            }
        });

        socketRef.current.on('toggle-upload-webchat', (data) => { setPermitirUpload(data.allow); });

        socketRef.current.on('whatsapp-profile-pic', (data) => {
            let pic = data.profilePicUrl === 'no_pic' ? null : data.profilePicUrl;
            setConversas(prev => prev.map(c => cleanJid(c.remote_jid) === cleanJid(data.remote_jid) ? { ...c, profilePicUrl: pic } : c));
            setSelectedChat(prev => prev && cleanJid(prev.remote_jid) === cleanJid(data.remote_jid) ? { ...prev, profilePicUrl: pic } : prev);
        });

        socketRef.current.on('whatsapp-new-message', (data) => {
            const incomingJid = data.remoteJid || data.remote_jid;
            if (!incomingJid) return;
            const normalizedIncoming = cleanJid(incomingJid);

            const isWebMsg =
                data.isWebChat === true ||
                data.channel === 'webchat' ||
                normalizedIncoming.startsWith('web_');

            const isGroup =
                String(incomingJid).includes('@g.us') ||
                data.chat_type === 'group' ||
                data.isGroup === true ||
                data.is_group === true;

            const normalizedMsg = {
                ...data,
                from_me: (data.fromMe === true || data.fromMe === 1 || data.from_me === 1) ? 1 : 0,
                text: data.text || data.content || '',
                mediaUrl: data.mediaUrl || data.media_url,
                msg_id: data.msg_id || data.key?.id,
                status: data.status || (data.fromMe ? 'SENT' : 'READ'),
                statusCode: data.statusCode || data.status_code || (data.fromMe ? 2 : 4),
                isWebChat: isWebMsg,
                quoted_content: data.quoted_content || data.quotedText || data.quoted?.body,
                quoted_participant: data.quoted_participant || data.quotedParticipant || data.quoted?.participant,
                tag_color: data.tagColor || data.tag_color
            };

            const isFromMe = normalizedMsg.from_me === 1;
            const isEvent = normalizedMsg.type === 'event' || data.type === 'event' || !!data.event_type || !!data.tipo_evento;

            let nomeExtraido = '';
            if (!isEvent && !isGroup) {
                nomeExtraido = data.contactName || data.pushName || '';
            }

            if (!isFromMe && !isEvent) {
                notifySoundRef.current?.play().catch(() => {});
            }

            setConversas(prev => {
                const existingChatIndex = prev.findIndex(c => cleanJid(c.remote_jid) === normalizedIncoming);

                if (existingChatIndex === -1 && isEvent) return prev;

                let updatedConversas = [...prev];
                const chatAbertoAtual = selectedChatRef.current;
                const isChatAberto = chatAbertoAtual && cleanJid(chatAbertoAtual.remote_jid) === normalizedIncoming;

                if (existingChatIndex !== -1) {
                    const chatAntigo = prev[existingChatIndex];
                    const chatIsGroup = String(chatAntigo.remote_jid || '').includes('@g.us') || chatAntigo.chat_type === 'group' || chatAntigo.isGroup === true || isGroup;

                    let nomeParaExibir = chatAntigo.contactName || chatAntigo.subject || chatAntigo.groupName || cleanJid(chatAntigo.remote_jid);

                    if (!chatIsGroup && !isEvent) {
                        if (nomeParaExibir === 'Atendente' || nomeParaExibir === 'Sistema') {
                            nomeParaExibir = cleanJid(chatAntigo.remote_jid);
                        }
                        if (!isFromMe && nomeExtraido && nomeExtraido !== 'Atendente' && nomeExtraido !== 'Sistema') {
                            if (!nomeParaExibir || nomeParaExibir === cleanJid(chatAntigo.remote_jid)) {
                                nomeParaExibir = nomeExtraido;
                            } else if (data.contactName && data.contactName !== nomeParaExibir && data.contactName !== cleanJid(chatAntigo.remote_jid)) {
                                nomeParaExibir = data.contactName;
                            }
                        }
                    }

                    if (chatIsGroup) {
                        nomeParaExibir = chatAntigo.contactName || chatAntigo.subject || chatAntigo.groupName || cleanJid(chatAntigo.remote_jid);
                    }

                    if (isEvent) {
                        nomeParaExibir = chatAntigo.contactName || chatAntigo.subject || chatAntigo.groupName || cleanJid(chatAntigo.remote_jid);
                    }

                    const updatedChat = {
                        ...chatAntigo,
                        contactName: nomeParaExibir,
                        profilePicUrl: (data.profilePicUrl && data.profilePicUrl !== 'no_pic') ? data.profilePicUrl : chatAntigo.profilePicUrl,
                        lastMsg: isEvent ? chatAntigo.lastMsg : (normalizedMsg.text || "Mídia"),
                        type: isEvent ? chatAntigo.type : normalizedMsg.type,
                        timestamp: isEvent ? chatAntigo.timestamp : (data.timestamp || Date.now()),
                        from_me: isEvent ? chatAntigo.from_me : normalizedMsg.from_me,
                        isWebChat: isWebMsg,
                        unreadCount: (normalizedMsg.from_me || isChatAberto || isEvent) ? 0 : (chatAntigo.unreadCount || 0) + 1
                    };

                    updatedConversas[existingChatIndex] = updatedChat;

                    const map = new Map();
                    updatedConversas.forEach(c => {
                        const key = cleanJid(c.remote_jid);
                        if (!map.has(key) || c.timestamp > map.get(key).timestamp) {
                            map.set(key, c);
                        }
                    });

                    const finalArray = Array.from(map.values());
                    if (isEvent) return finalArray;

                    return finalArray.sort((a, b) => {
                        if (a.is_favorite === b.is_favorite) return b.timestamp - a.timestamp;
                        return (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0);
                    });
                }

                let novoNome;
                if (isGroup) {
                    novoNome = data.subject || data.groupName || data.group_name || cleanJid(incomingJid);
                } else {
                    novoNome = cleanJid(incomingJid);
                    if (!isFromMe && nomeExtraido && nomeExtraido !== 'Atendente' && nomeExtraido !== 'Sistema') {
                        novoNome = nomeExtraido;
                    }
                }

                const novoChat = {
                    remote_jid: incomingJid,
                    contactName: novoNome,
                    profilePicUrl: (data.profilePicUrl && data.profilePicUrl !== 'no_pic') ? data.profilePicUrl : null,
                    lastMsg: normalizedMsg.text || "Mídia",
                    type: normalizedMsg.type,
                    timestamp: data.timestamp || Date.now(),
                    from_me: normalizedMsg.from_me,
                    tags: data.tags || [],
                    isWebChat: isWebMsg,
                    is_favorite: false,
                    unreadCount: (normalizedMsg.from_me || isChatAberto) ? 0 : 1,
                    chat_type: isGroup ? 'group' : (data.chat_type || 'conversation')
                };

                const firstNonFavoriteIndex = updatedConversas.findIndex(c => !c.is_favorite);
                if (firstNonFavoriteIndex === -1) {
                    updatedConversas.push(novoChat);
                } else {
                    updatedConversas.splice(firstNonFavoriteIndex, 0, novoChat);
                }

                const map = new Map();
                updatedConversas.forEach(c => {
                    const key = cleanJid(c.remote_jid);
                    if (!map.has(key) || c.timestamp > map.get(key).timestamp) map.set(key, c);
                });

                return Array.from(map.values()).sort((a, b) => {
                    if (a.is_favorite === b.is_favorite) return b.timestamp - a.timestamp;
                    return (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0);
                });
            });

            const chatAbertoAtual = selectedChatRef.current;
            const isChatAberto = chatAbertoAtual && cleanJid(chatAbertoAtual.remote_jid) === normalizedIncoming;

            if (isChatAberto) {
                const selectedIsGroup = String(chatAbertoAtual.remote_jid || '').includes('@g.us') || chatAbertoAtual.chat_type === 'group' || isGroup;

                if (!selectedIsGroup && !isEvent && (chatAbertoAtual.contactName === 'Atendente' || chatAbertoAtual.contactName === 'Sistema')) {
                    setSelectedChat(prev => ({ ...prev, contactName: cleanJid(prev.remote_jid) }));
                }

                setMensagens(prevMsgs => {
                    const jaExisteId = prevMsgs.find(m => m.msg_id === normalizedMsg.msg_id);
                    if (jaExisteId) return prevMsgs;

                    if (normalizedMsg.from_me) {
                        if (normalizedMsg.requestId) {
                            const indexTemp = prevMsgs.findIndex(m => m.msg_id === normalizedMsg.requestId);
                            if (indexTemp !== -1) {
                                const newMsgs = [...prevMsgs];
                                newMsgs[indexTemp] = normalizedMsg;
                                return newMsgs;
                            }
                        }

                        const duplicadaOtimistaIndex = prevMsgs.findIndex(m =>
                            String(m.msg_id).startsWith('temp_') &&
                            (m.text === normalizedMsg.text || ['image','video','audio','document'].includes(m.type)) &&
                            (Date.now() - m.timestamp < 60000)
                        );

                        if (duplicadaOtimistaIndex !== -1) {
                            const newMsgs = [...prevMsgs];
                            newMsgs[duplicadaOtimistaIndex] = normalizedMsg;
                            return newMsgs;
                        }
                    }

                    return [...prevMsgs, normalizedMsg];
                });
            } else if (!normalizedMsg.from_me && !isEvent) {
                let notifyName;
                if (isGroup) {
                    const conversaGrupo = conversas.find(c => cleanJid(c.remote_jid) === normalizedIncoming);
                    notifyName = conversaGrupo?.contactName || data.subject || data.groupName || data.group_name || 'Grupo';
                } else {
                    notifyName = data.contactName || nomeExtraido || cleanJid(incomingJid);
                }

                toast((t) => (
                    <div className="wa-toast-notif" onClick={() => {
                        const conversaAtual = conversas.find(c => cleanJid(c.remote_jid) === normalizedIncoming);
                        carregarHistorico({
                            remote_jid: incomingJid,
                            contactName: isGroup ? (conversaAtual?.contactName || data.subject || data.groupName || data.group_name || notifyName) : notifyName,
                            profilePicUrl: data.profilePicUrl,
                            isWebChat: isWebMsg,
                            chat_type: isGroup ? 'group' : data.chat_type
                        });
                        toast.dismiss(t.id);
                    }}>
                        <div className="wa-toast-avatar">
                            {data.profilePicUrl && data.profilePicUrl !== 'no_pic' ? (
                                <img src={data.profilePicUrl} alt="avatar" referrerPolicy="no-referrer" className="wa-avatar-img" />
                            ) : (
                                <div className="wa-avatar-placeholder">
                                    {String(notifyName || 'C').charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="wa-toast-body">
                            <strong>{notifyName}</strong>
                            <p>{normalizedMsg.text ? (normalizedMsg.text.substring(0, 45) + '...') : 'Enviou uma mídia'}</p>
                        </div>
                    </div>
                ), { duration: 4000, position: 'top-right', style: { zIndex: 99999 } });
            }
        });

        socketRef.current.on('whatsapp-status', (data) => {
            carregarInstancias(); 
        });

        carregarConversas();
        carregarTemplates();

        return () => { if (socketRef.current) socketRef.current.disconnect(); };
    }, [estabelecimentoId]);

    const carregarTags = async () => {
        try {
            const res = await api.get(`/whatsapp/tags/${estabelecimentoId}`);
            if (res.data && res.data.payload) setAvailableTags(res.data.payload);
        } catch (err) { }
    };

    const handleCreateTag = async (e) => {
        e.preventDefault();
        if (!newTagData.nome) return toast.error("Digite um nome para a tag");
        try {
            const res = await api.post(`/whatsapp/tags/${estabelecimentoId}`, newTagData);
            if (res.data.success) {
                setAvailableTags([...availableTags, res.data.payload]);
                setNewTagData({ nome: '', cor: '#00a884', descricao: '' });
                toast.success("Tag criada!");
            }
        } catch (err) { toast.error("Erro ao criar tag"); }
    };

    const handleDeleteTagSystem = (tagId) => {
        setConfirmModal({
            show: true, title: 'Excluir Tag do Sistema', message: 'Isso excluirá a tag de todos os contatos vinculados. Tem certeza?',
            onConfirm: async () => {
                try {
                    await api.delete(`/whatsapp/tags/${estabelecimentoId}/${tagId}`);
                    setAvailableTags(availableTags.filter(t => t.id !== tagId));
                    toast.success("Tag removida do sistema");

                    setConversas(prev => prev.map(c => ({
                        ...c, tags: (c.tags || []).filter(t => t.id !== tagId)
                    })));
                    setSelectedChat(prev => {
                        if (!prev) return prev;
                        return { ...prev, tags: (prev.tags || []).filter(t => t.id !== tagId) };
                    });
                } catch (err) { toast.error("Erro ao excluir tag"); }
            }
        });
    };

    const startEditing = (tag) => { setEditingTag(tag.id); setEditTagData({ nome: tag.nome, cor: tag.cor, descricao: tag.descricao || '' }); };

    const handleUpdateTag = async (e) => {
        e.preventDefault();
        try {
            const res = await api.put(`/whatsapp/tags/${estabelecimentoId}/${editingTag}`, editTagData);
            if (res.data.success) {
                setAvailableTags(availableTags.map(t => t.id === editingTag ? { ...t, ...editTagData } : t));
                setEditingTag(null);
                toast.success("Tag atualizada!");
                carregarConversas(); 
            }
        } catch (err) { toast.error("Erro ao atualizar tag"); }
    };

    const handleToggleTag = async (tag) => {
        if (!selectedChat) return;
        const hasTag = selectedChat.tags?.some(t => t.id === tag.id);
        const cleanWhatsappId = cleanJid(selectedChat.remote_jid);

        try {
            let nomeReal = getSafeUserName();

            const newTags = hasTag ? (selectedChat.tags || []).filter(t => t.id !== tag.id) : [...(selectedChat.tags || []), tag];
            setSelectedChat(prev => ({ ...prev, tags: newTags }));
            setConversas(prev => prev.map(c => cleanJid(c.remote_jid) === cleanJid(selectedChat.remote_jid) ? { ...c, tags: newTags } : c));

            if (hasTag) {
                await api.delete(`/whatsapp/tags-client/${estabelecimentoId}`, { data: { whatsapp: cleanWhatsappId, remoteJid: selectedChat.remote_jid, tagId: tag.id, nomeTag: tag.nome, operador: nomeReal, corTag: tag.cor } });
                toast.success("Tag removida do contato");
            } else {
                await api.post(`/whatsapp/tags-client/${estabelecimentoId}`, { whatsapp: cleanWhatsappId, remoteJid: selectedChat.remote_jid, tagId: tag.id, nomeTag: tag.nome, corTag: tag.cor, operador: nomeReal, descricaoTag: motivoTag });
                toast.success("Tag adicionada ao contato");
            }
            setMotivoTag('');
        } catch (err) { toast.error("Erro ao gerenciar tag."); }
    };

    const carregarTemplates = async () => {
        try {
            const res = await api.get(`/whatsapp/templates/${estabelecimentoId}`);
            if (res.data && res.data.payload) setTemplates(res.data.payload);
        } catch (err) { }
    };

    const carregarHistorico = async (chat) => {
        setConversas(prev =>
            prev.map(c =>
                cleanJid(c.remote_jid) === cleanJid(chat.remote_jid)
                    ? { ...c, unreadCount: 0 }
                    : c
            )
        );

        let safeName = chat.contactName;
        if (safeName === 'Atendente' || safeName === 'Sistema') {
            safeName = cleanJid(chat.remote_jid);
        }

        const remoteJid = String(chat.remote_jid || '');
        const isGroup = remoteJid.endsWith('@g.us') || chat.chat_type === 'group';
        const isNewsletter = remoteJid.endsWith('@newsletter') || chat.chat_type === 'newsletter';
        const isWeb = chat.isWebChat === true || chat.channel === 'webchat' || remoteJid.startsWith('web_');

        let chatInstanceId = chat.instance_id ? Number(chat.instance_id) : null;

        if (!isWeb && (isGroup || isNewsletter) && chatInstanceId) {
            const instanceExists = instances.find(i => Number(i.id) === Number(chatInstanceId));
            if (instanceExists) {
                setSelectedInstanceId(Number(chatInstanceId));
            }
        }

        setSelectedChat({
            ...chat,
            contactName: safeName,
            instance_id: chatInstanceId || null
        });

        setReplyingTo(null);
        setActiveMessageMenu(null);
        setShowActionMenu(false);

        if (isMobile) {
            setRightPanelOpen(false);
        }

        setCurrentChannel(isWeb ? 'webchat' : 'whatsapp');
        const jidLimpo = cleanJid(chat.remote_jid);

        try {
            const resMsg = await api.get(
                `/whatsapp/history/${estabelecimentoId}?remoteJid=${encodeURIComponent(chat.remote_jid)}`
            );

            let normalizedHistory = [];

            if (resMsg.data && resMsg.data.payload) {
                normalizedHistory = resMsg.data.payload.map(msg => ({
                    ...msg,
                    status: msg.status || msg.statusCode || (msg.from_me ? 'SENT' : 'READ'),
                    statusCode: msg.statusCode || msg.status_code || msg.status,
                    quoted_content: msg.quoted_content || msg.quotedText || msg.quoted?.body,
                    quoted_participant: msg.quoted_participant || msg.quotedParticipant || msg.quoted?.participant
                }));

                setMensagens(normalizedHistory);

                // Dispara marcação como lido no backend
                try {
                    const unreadMsgs = normalizedHistory
                        .filter(m => !m.from_me)
                        .map(m => ({ id: m.msg_id, participant: m.original_participant }));

                    if (unreadMsgs.length > 0) {
                        api.post(`/whatsapp/mark-read/${estabelecimentoId}`, {
                            remoteJid: chat.remote_jid,
                            messages: unreadMsgs,
                            instanceId: chatInstanceId
                        }).catch(err => console.log(err));
                    }
                } catch (err) {}

                if (!isWeb && (isGroup || isNewsletter) && !chatInstanceId) {
                    const msgComInstance = [...normalizedHistory].reverse().find(m => m.instance_id !== null && m.instance_id !== undefined);
                    if (msgComInstance && msgComInstance.instance_id) {
                        const historyInstanceId = Number(msgComInstance.instance_id);
                        const instanceExists = instances.find(i => Number(i.id) === historyInstanceId);
                        if (instanceExists) {
                            chatInstanceId = historyInstanceId;
                            setSelectedInstanceId(historyInstanceId);
                            setSelectedChat(prev => ({ ...prev, instance_id: historyInstanceId }));
                            setConversas(prev =>
                                prev.map(c => cleanJid(c.remote_jid) === cleanJid(chat.remote_jid) ? { ...c, instance_id: historyInstanceId } : c)
                            );
                        }
                    }
                }
            }

            if (isGroup || isNewsletter) {
                try {
                    const resData = await api.get(`/whatsapp/whats-details/${estabelecimentoId}?whatsapp=${encodeURIComponent(remoteJid)}`);
                    if (resData.data?.payload && resData.data.payload.id) {
                        const dados = resData.data.payload;
                        setContactExists(true);
                        setContactData({
                            ...dados,
                            id: dados.id,
                            nome: dados.nome || safeName || (isNewsletter ? 'Canal' : 'Grupo'),
                            whatsapp: remoteJid,
                            remoteJidOriginal: remoteJid
                        });

                        if (dados.nome && String(dados.nome).trim() !== '') {
                            setSelectedChat(prev => (!prev ? prev : { ...prev, contactName: dados.nome, instance_id: chatInstanceId || prev.instance_id || null }));
                            setConversas(prev => prev.map(c => cleanJid(c.remote_jid) === cleanJid(remoteJid) ? { ...c, contactName: dados.nome, instance_id: chatInstanceId || c.instance_id || null } : c));
                        }
                    } else {
                        setContactExists(false);
                        setContactData({
                            id: null, nome: safeName || (isNewsletter ? 'Canal' : 'Grupo'),
                            email: '', cpf: '', whatsapp: remoteJid,
                            cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
                            coringa1: '', coringa2: '', coringa3: '', remoteJidOriginal: remoteJid
                        });
                    }
                } catch (detailErr) {
                    setContactExists(false);
                    setContactData({
                        id: null, nome: safeName || (isNewsletter ? 'Canal' : 'Grupo'),
                        email: '', cpf: '', whatsapp: remoteJid,
                        cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
                        coringa1: '', coringa2: '', coringa3: '', remoteJidOriginal: remoteJid
                    });
                }
                return;
            }

            const whatsappConsulta = jidLimpo;
            const resCont = await api.get(`/whatsapp/check-contact/${estabelecimentoId}?whatsapp=${encodeURIComponent(whatsappConsulta)}`);
            setContactExists(resCont.data.exists);

            if (resCont.data.exists) {
                const resData = await api.get(`/whatsapp/whats-details/${estabelecimentoId}?whatsapp=${encodeURIComponent(whatsappConsulta)}`);
                if (resData.data?.payload) {
                    const dados = resData.data.payload;
                    setContactData({
                        ...dados,
                        remoteJidOriginal: dados.remoteJidOriginal || dados.remote_jid_original || (isWeb ? remoteJid : '')
                    });
                }
            } else {
                setContactData({
                    id: null,
                    nome: safeName || 'Visitante',
                    email: '',
                    cpf: '',
                    whatsapp: isWeb ? '' : jidLimpo,
                    cep: '',
                    endereco: '',
                    numero: '',
                    complemento: '',
                    bairro: '',
                    cidade: '',
                    estado: '',
                    coringa1: '',
                    coringa2: '',
                    coringa3: '',
                    remoteJidOriginal: isWeb ? remoteJid : ''
                });
            }

        } catch (err) {
            console.error('[CHAT] Erro ao carregar detalhes da conversa:', err);
            toast.error("Erro ao carregar detalhes da conversa.");
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileToUpload(file); setFileCaption(newMessage); setFilePreviewUrl(URL.createObjectURL(file));
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60); const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleSend = async (e) => {
        if (e) e.preventDefault();
        const file = fileToUpload;
        const textToProcess = file ? fileCaption : newMessage;
        if (!textToProcess.trim() && !file) return;

        let targetJid = selectedChat?.remote_jid;
        if (currentChannel === 'webchat') {
            if (!String(targetJid).startsWith('web_') && contactData?.remoteJidOriginal?.startsWith('web_')) targetJid = contactData.remoteJidOriginal;
        } else if (currentChannel === 'whatsapp') {
            if (String(targetJid).startsWith('web_')) {
                if (contactData?.whatsapp) {
                    const numLimpo = contactData.whatsapp.replace(/\D/g, ''); targetJid = numLimpo.includes('@') ? numLimpo : `${numLimpo}@s.whatsapp.net`;
                } else {
                    return toast.error("Este visitante não possui WhatsApp cadastrado. Retorne ao Webchat para enviar!");
                }
            }
        }

        let tempNomeOp = nomeOperador;
        if (!tempNomeOp || tempNomeOp === 'Sistema') tempNomeOp = getSafeUserName();

        const prefixo = exibirNomeOperador ? `*[${tempNomeOp}]*\n` : "";
        const textoComVariaveis = processarVariaveis(textToProcess);
        const textoFinal = prefixo + textoComVariaveis;
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`; 

        let msgType = currentChannel === 'webchat' ? 'webchat' : 'conversation';
        if (file) {
            if (file.type.startsWith('image/')) msgType = 'image'; else if (file.type.startsWith('video/')) msgType = 'video';
            else if (file.type.startsWith('audio/')) msgType = 'audio'; else msgType = 'document';
        }

        const currentReplying = replyingTo;
        let sentQuotedParticipant = null;
        if (currentReplying) {
            if (currentReplying.from_me) sentQuotedParticipant = 'Você';
            else sentQuotedParticipant = selectedChat?.contactName || 'Cliente';
        }

        // Adiciona otimisticamente na tela
        const localMsg = {
            msg_id: tempId, remote_jid: targetJid, text: textoFinal, from_me: 1, timestamp: Date.now(), type: msgType, isWebChat: currentChannel === 'webchat',
            mediaUrl: filePreviewUrl, quoted_content: currentReplying ? (currentReplying.text || currentReplying.content || 'Mídia') : null,
            quoted_participant: sentQuotedParticipant, status: 'PENDING', statusCode: 0
        };

        setMensagens(prev => [...prev, localMsg]);
        setNewMessage(""); setFileToUpload(null); setFilePreviewUrl(null); setFileCaption(""); setReplyingTo(null); setShowActionMenu(false);

        const formData = new FormData();
        formData.append('number', targetJid); 
        formData.append('text', textoFinal); 
        formData.append('channel', currentChannel); 
        formData.append('requestId', tempId); 
        formData.append('pushName', contactData?.nome || selectedChat?.contactName || '');

        if (selectedInstanceId && currentChannel === 'whatsapp') {
            formData.append('instanceId', selectedInstanceId);
        }

        if (currentReplying) formData.append('replyTo', currentReplying.msg_id || currentReplying.key?.id);
        if (file) formData.append('media', file);

        try {
            const response = await api.post(`/whatsapp/send/${estabelecimentoId}`, formData);
            const newMsgId = response?.data?.payload?.msg_id || response?.data?.msg_id;

            // 🔥 Atualiza imediatamente para ENVIADO (1 Tique Cinza)
            if (newMsgId) {
                setMensagens(prev => prev.map(m => m.msg_id === tempId ? { ...m, msg_id: newMsgId, status: 'SENT', statusCode: 2 } : m));
            } else {
                setMensagens(prev => prev.map(m => m.msg_id === tempId ? { ...m, status: 'SENT', statusCode: 2 } : m));
            }

            setConversas(prev => {
                const map = new Map();
                prev.forEach(c => map.set(cleanJid(c.remote_jid), c));
                const targetClean = cleanJid(selectedChat.remote_jid);
                if (map.has(targetClean)) {
                    map.set(targetClean, { ...map.get(targetClean), lastMsg: textoFinal || "Mídia", timestamp: Date.now(), from_me: 1, unreadCount: 0 });
                }
                return Array.from(map.values()).sort((a, b) => b.timestamp - a.timestamp);
            });

        } catch (err) {
            setMensagens(prev => prev.filter(m => m.msg_id !== tempId)); 
            setNewMessage(textToProcess); 
            setReplyingTo(currentReplying);
            toast.error(`Falha no envio: ${err.response?.data?.error || "Erro 502"}`);
        }
    };

    const handleCepChange = async (e) => {
        const cep = e.target.value.replace(/\D/g, "");
        setContactData({ ...contactData, cep });
        if (cep.length === 8) {
            setLoadingCep(true);
            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                const data = await response.json();
                if (!data.erro) setContactData(prev => ({ ...prev, endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf }));
            } finally { setLoadingCep(false); }
        }
    };

    const handleContextMenu = (e, chat) => { 
        e.preventDefault(); 
        const clientY = e.clientY || (e.touches && e.touches[0].clientY) || 0;
        const clientX = e.clientX || (e.touches && e.touches[0].clientX) || 0;

        const menuHeight = 220; 
        const menuWidth = 220;

        let y = clientY;
        let x = clientX;

        if (window.innerHeight - clientY < menuHeight) {
            y = clientY - menuHeight;
        }

        if (window.innerWidth - clientX < menuWidth) {
            x = window.innerWidth - menuWidth - 10;
        }

        setContextMenu({ show: true, x: Math.max(10, x), y: Math.max(10, y), chat }); 
    };

    const handleDeleteChat = (chat) => {
        setConfirmModal({
            show: true, title: 'Excluir Histórico', message: `Deseja apagar definitivamente a conversa com ${chat.contactName}?`,
            onConfirm: async () => {
                try {
                    const jidEncoded = encodeURIComponent(chat.remote_jid);
                    await api.delete(`/whatsapp/chat/${estabelecimentoId}/${jidEncoded}`);
                    toast.success("Conversa excluída!");
                    carregarConversas();
                    if (selectedChat?.remote_jid === chat.remote_jid) { setSelectedChat(null); setMensagens([]); }
                } catch (err) { toast.error("Erro ao excluir conversa."); }
            }
        });
    };

    const handleMarkAsRead = async (chat) => {
        setConversas(prev => prev.map(c => cleanJid(c.remote_jid) === cleanJid(chat.remote_jid) ? { ...c, unreadCount: 0 } : c));
        toast.success("Conversa marcada como lida!"); 
        setContextMenu({ ...contextMenu, show: false });

        try {
            const unreadMsgs = mensagens
                .filter(m => !m.from_me)
                .map(m => ({ id: m.msg_id, participant: m.original_participant }));

            if (unreadMsgs.length > 0) {
                await api.post(`/whatsapp/mark-read/${estabelecimentoId}`, {
                    remoteJid: chat.remote_jid,
                    messages: unreadMsgs,
                    instanceId: chat.instance_id
                });
            }
        } catch (err) {
            console.error("Erro ao enviar visto azul", err);
        }
    };

    const formatarTextoWhatsApp = (texto) => {
        if (!texto) return "";
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        let formatted = texto
            .replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #00a5ff; text-decoration: underline; word-break: break-all;">$1</a>')
            .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
            .replace(/_(.*?)_/g, '<em>$1</em>')
            .replace(/~(.*?)~/g, '<del>$1</del>')
            .replace(/```(.*?)```/g, '<code>$1</code>')
            .replace(/\n/g, '<br/>');

        const sortedContacts = [...globalContacts].sort((a, b) => (b.nome?.length || 0) - (a.nome?.length || 0));
        sortedContacts.forEach(c => {
            if (c.nome && c.nome.trim() !== '') {
                const safeName = c.nome.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(^|\\s)@(${safeName})(?=[\\s,.:;!?]|$)`, 'g');
                formatted = formatted.replace(regex, `$1<span class="mention-tag" data-mention="$2" style="color: #00a5ff; font-weight: 600; cursor: pointer;">@$2</span>`);
            }
        });

        formatted = formatted.replace(/(^|\s)@([a-zA-ZÀ-ÿ0-9_ .()\[\]\u00a9-\u024f\u2000-\u3300\uD83C-\uD83E\uDC00-\uDFFF\u2600-\u26FF]+)/g, (match, p1, p2) => {
            if (match.includes('<span')) return match; 
            const cleanMention = p2.trim();
            const resolved = resolveContactName(cleanMention);
            const display = resolved ? resolved : cleanMention;
            return `${p1}<span class="mention-tag" data-mention="${display}" style="color: #00a5ff; font-weight: 600; cursor: pointer;">@${display}</span>`;
        });

        return formatted;
    };

    const extractUrlFromText = (text) => {
        if (!text) return null;
        const match = text.match(/(https?:\/\/[^\s]+)/);
        return match ? match[0] : null;
    };

    const getFileNameFromUrl = (fileUrl) => {
        if (!fileUrl) return "Arquivo";
        try {
            const parts = fileUrl.split('?')[0].split('/');
            let decodedName = decodeURIComponent(parts[parts.length - 1]) || "Arquivo";
            return decodedName.replace(/^\d+[_|-]/, '');
        } catch { return "Arquivo"; }
    };

    const isImageUrl = (url) => {
        if (!url) return false;
        const lowerUrl = url.toLowerCase();
        return lowerUrl.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i) || lowerUrl.includes('image') || lowerUrl.startsWith('blob:') || lowerUrl.includes('pps.whatsapp.net'); 
    };

    const ActionMenuButton = ({ icon: Icon, label, onClick, color = "#00a884" }) => (
        <button
            type="button" onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'transparent', border: 'none',
                borderRadius: '8px', cursor: 'pointer', color: '#54656f', fontWeight: '500', transition: 'all 0.2s ease', width: '100%', textAlign: 'left', whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.9)'; e.currentTarget.style.color = color; e.currentTarget.style.boxShadow = '0 2px 5px rgba(0,0,0,0.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#54656f'; e.currentTarget.style.boxShadow = 'none'; }}
        >
            <Icon size={18} /> {label}
        </button>
    );

    const getChatCategory = (c) => {
        const jid = String(c.remote_jid || '').toLowerCase();
        const type = String(c.chat_type || '').toLowerCase();
        const channel = String(c.channel || '').toLowerCase();

        if (jid.includes('@newsletter') || type === 'newsletter') return 'canais';
        if (jid.includes('@g.us') || type === 'group' || jid.startsWith('120')) return 'grupos';
        if (jid.startsWith('web_') || c.isWebChat === true || channel === 'webchat' || type === 'webchat') return 'webchat';
        return 'whatsapp';
    };

    const unreadWhatsApp = conversas.reduce((acc, c) => getChatCategory(c) === 'whatsapp' ? acc + (c.unreadCount || 0) : acc, 0);
    const unreadWebchat = conversas.reduce((acc, c) => getChatCategory(c) === 'webchat' ? acc + (c.unreadCount || 0) : acc, 0);
    const unreadGrupos = conversas.reduce((acc, c) => getChatCategory(c) === 'grupos' ? acc + (c.unreadCount || 0) : acc, 0);
    const unreadCanais = conversas.reduce((acc, c) => getChatCategory(c) === 'canais' ? acc + (c.unreadCount || 0) : acc, 0);

    const conversasFiltradas = conversas.filter(c => {
        const remoteJidStr = String(c.remote_jid || '').toLowerCase();
        const contactNameStr = String(c.contactName || '').toLowerCase();
        const contactEmailStr = String(contactData.email || '').toLowerCase(); 
        const contactCpfStr = String(contactData.cpf || '').toLowerCase();

        const term = searchTerm.toLowerCase();
        const matchesSearch = term === "" || 
                              contactNameStr.includes(term) || 
                              remoteJidStr.includes(term) || 
                              contactEmailStr.includes(term) || 
                              contactCpfStr.includes(term);

        if (!matchesSearch) return false;

        if (selectedTagFilter) {
            if (selectedTagFilter === 'unread' && c.unreadCount === 0) return false;
            if (selectedTagFilter === 'favorites' && !c.is_favorite) return false;
            if (typeof selectedTagFilter === 'number' && (!c.tags || !c.tags.some(t => t.id === selectedTagFilter))) return false;
        }

        return getChatCategory(c) === activeTab;
    });

    const isChannelChat = selectedChat?.remote_jid?.includes('@newsletter') || selectedChat?.chat_type === 'newsletter';

    const getQuotedName = (participant, from_me) => {
        if (!participant) return 'Mensagem citada';
        if (participant.toLowerCase() === 'você' || participant.toLowerCase() === 'voce') return 'Você';
        if (from_me) return 'Você';

        const cleanPart = cleanJid(participant);
        const cleanChat = cleanJid(selectedChat?.remote_jid);

        if (cleanPart === cleanChat && selectedChat?.contactName) return selectedChat.contactName;

        const resolved = resolveContactName(cleanPart);
        if (resolved) return resolved;

        return `+${participant.split('@')[0].replace(/^\++/, '')}`;
    };

    const renderMessageContentNormal = (m, index) => {
        const mediaPath = m.mediaUrl || m.media_url || m.url || m.fileUrl || m.documentUrl;
        const url = mediaPath ? (mediaPath.startsWith('http') || mediaPath.startsWith('blob:') || mediaPath.startsWith('data:') ? mediaPath : `${BASE_URL}${mediaPath}`) : null;
        const textContent = m.text || m.content || "";
        const type = String(m.type || '').toLowerCase();

        const isImage = type.includes('image') || (url && url.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i));
        const isVideo = type.includes('video') || (url && url.match(/\.(mp4|mov|avi)($|\?)/i));
        const isAudio = type.includes('audio') || type.includes('ptt') || type.includes('audiomessage') || (url && url.match(/\.(ogg|mp3|wav|m4a|opus)($|\?)/i));

        const textIsFilename = textContent && textContent.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|txt|csv)$/i);
        const typeIsDoc = type.includes('document') || type.includes('file');
        const urlIsDoc = url && url.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|txt|csv)($|\?)/i);

        const isDoc = (typeIsDoc || urlIsDoc || textIsFilename || (url && !isImage && !isVideo && !isAudio)) && !isAudio;

        const downloadFileUrl = url || (extractUrlFromText(textContent));
        const displayFileName = textIsFilename ? textContent : getFileNameFromUrl(downloadFileUrl || url);
        const textToRender = (isDoc && (textContent === displayFileName || textContent === downloadFileUrl)) ? "" : textContent;

        const activeLinkUrl = m.link_url || extractUrlFromText(textToRender);
        const msgUniqueId = m.msg_id || m.key?.id || index;

        const isGroup = selectedChat?.remote_jid?.includes('@g.us') || selectedChat?.chat_type === 'group';
        const participantRaw = m.original_participant || m.quoted_participant || (m.participant_phone ? `${m.participant_phone}@s.whatsapp.net` : null);
        const participantPhoneStr = participantRaw ? participantRaw.split('@')[0].replace(/\D/g, '') : (m.participant_phone || '');

        let resolvedName = resolveContactName(participantPhoneStr);
        let dispName = resolvedName || m.participant_name || m.pushName || m.contactName || participantPhoneStr;

        let realPhoneToDisplay = participantPhoneStr;
        if (dispName !== participantPhoneStr) {
            const foundContact = globalContacts.find(c => c.nome === dispName || c.nome === m.participant_name);
            if (foundContact && foundContact.whatsapp) {
                realPhoneToDisplay = String(foundContact.whatsapp).replace(/\D/g, '');
            }
        }

        return (
            <div className="wa-message-content-wrapper" style={{ position: 'relative', paddingRight: '32px' }}>
                <div style={{ position: 'absolute', top: '2px', right: '4px', zIndex: 20 }}>
                    <button 
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            const rect = e.currentTarget.getBoundingClientRect();
                            if (activeMessageMenu?.id === msgUniqueId) { setActiveMessageMenu(null); } 
                            else {
                                const menuHeight = 180; const spaceBelow = window.innerHeight - rect.bottom;
                                const y = spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 4;
                                const x = rect.right - 160;
                                setActiveMessageMenu({ id: msgUniqueId, msg: m, x: Math.max(10, x), y });
                            }
                        }}
                        style={{ background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
                    >
                        <ChevronDown size={14} color="#54656f" />
                    </button>
                </div>

                {!m.from_me && isGroup && participantPhoneStr && (
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#00a884', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span title="Número do participante">~ {dispName !== realPhoneToDisplay ? `${dispName} (${realPhoneToDisplay})` : realPhoneToDisplay}</span>
                        <button 
                            type="button"
                            onClick={async (e) => {
                                e.stopPropagation();

                                try {
                                    const resData = await api.get(`/whatsapp/whats-details/${estabelecimentoId}?whatsapp=${participantPhoneStr}`);
                                    if (resData.data?.payload) {
                                        setContactData({
                                            ...resData.data.payload,
                                            remoteJidOriginal: participantPhoneStr 
                                        });
                                    } else {
                                        const localContact = globalContacts.find(c => {
                                            const cNum = String(c.whatsapp || c.telefone || c.remote_jid_original || '').replace(/\D/g, '');
                                            return cNum && participantPhoneStr && cNum.length >= 8 && participantPhoneStr.length >= 8 && cNum.slice(-8) === participantPhoneStr.slice(-8);
                                        });

                                        if (localContact) {
                                            setContactData({
                                                id: localContact.id, nome: localContact.nome, email: localContact.email || '', cpf: localContact.cpf || '', whatsapp: participantPhoneStr,
                                                cep: localContact.cep || '', endereco: localContact.endereco || '', numero: localContact.numero || '', complemento: localContact.complemento || '', bairro: localContact.bairro || '', cidade: localContact.cidade || '', estado: localContact.estado || '', coringa1: localContact.coringa1 || '', coringa2: localContact.coringa2 || '', coringa3: localContact.coringa3 || '',
                                                remoteJidOriginal: participantPhoneStr
                                            });
                                        } else {
                                            setContactData({
                                                id: null, nome: dispName !== participantPhoneStr ? dispName : '', email: '', cpf: '', whatsapp: '',
                                                cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', coringa1: '', coringa2: '', coringa3: '',
                                                remoteJidOriginal: participantPhoneStr 
                                            });
                                        }
                                    }
                                } catch(err) {
                                    setContactData({
                                        id: null, nome: dispName !== participantPhoneStr ? dispName : '', email: '', cpf: '', whatsapp: '',
                                        cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', coringa1: '', coringa2: '', coringa3: '',
                                        remoteJidOriginal: participantPhoneStr
                                    });
                                }
                                setShowAddContactModal(true);
                            }}
                            style={{ background: '#e0f2fe', border: 'none', cursor: 'pointer', color: '#0284c7', padding: '2px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            title="Cadastrar / Editar Contato"
                        >
                            <UserPlus size={12} /> Salvar/Ver
                        </button>
                    </div>
                )}

                {!m.from_me && !isGroup && participantPhoneStr && (
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#00a884', marginBottom: '2px' }}>~ {participantPhoneStr}</div>
                )}

                {m.quoted_content && (
                    <div 
                        onClick={() => scrollToQuotedMessage(m, index)}
                        style={{ borderLeft: '4px solid #00a884', background: m.from_me ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.05)', padding: '6px 10px', marginBottom: '6px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                        <span style={{ fontWeight: '600', color: m.from_me ? '#005c4b' : '#00a884', display: 'block', marginBottom: '2px' }}>
                            {getQuotedName(m.quoted_participant, m.from_me)}
                        </span>
                        <span style={{ color: m.from_me ? '#3b4a54' : '#555', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatarTextoWhatsApp(m.quoted_content), { ADD_ATTR: ['target', 'rel', 'data-mention'] }) }}>
                        </span>
                    </div>
                )}

                {isImage && url && (
                    <div className="wa-media-container"><img src={url} className="wa-chat-img clickable" alt="img" onClick={() => setPreviewImage(url)} style={{ cursor: 'pointer' }} /></div>
                )}
                {isVideo && url && (
                    <div className="wa-media-container" onClick={() => setPreviewImage(url)} style={{ cursor: 'pointer', position: 'relative' }}>
                        <video className="wa-chat-video" src={url} preload="metadata" />
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(0,0,0,0.4)', borderRadius: '50%', padding: '10px', display: 'flex' }}><Zap size={24} color="#fff" /></div>
                    </div>
                )}
                {isAudio && url && (
                    <audio controls className="wa-chat-audio"><source src={url} type="audio/ogg" /></audio>
                )}

                {isDoc && (
                    <div className="wa-media-doc" style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(0,0,0,0.04)', padding: '10px 12px', borderRadius: '8px', marginBottom: textToRender ? '8px' : '0' }}>
                        <FileText size={32} color="#00a884" style={{ flexShrink: 0 }} />
                        <div className="wa-doc-info" style={{ flex: 1, minWidth: '0' }}>
                            <span style={{ fontWeight: '500', fontSize: '13px', color: '#111b21', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }} title={displayFileName}>
                                {displayFileName}
                            </span>
                            {downloadFileUrl && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '2px' }}>
                                    <span onClick={() => setPreviewImage(downloadFileUrl)} style={{ fontSize: '11px', color: '#00a5ff', cursor: 'pointer', textDecoration: 'underline' }}>Visualizar preview</span>
                                </div>
                            )}
                        </div>
                        {downloadFileUrl ? (
                            <a 
                                href={downloadFileUrl} 
                                download={displayFileName} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                style={{ color: '#54656f', display: 'flex', alignItems: 'center', padding: '6px', cursor: 'pointer' }}
                                title="Baixar arquivo"
                            >
                                <Download size={20} />
                            </a>
                        ) : (
                            <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>S/ Link</span>
                        )}
                    </div>
                )}

                {textToRender && (
                    <p className="wa-message-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatarTextoWhatsApp(textToRender), { ADD_ATTR: ['target', 'rel', 'data-mention'] }) }} />
                )}

                {activeLinkUrl && (
                    <a href={activeLinkUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none', border: '1px solid #e1e9eb', borderRadius: '10px', overflow: 'hidden', marginBottom: '4px' }}>
                        {m.link_image ? (
                            <img src={m.link_image.startsWith('http') || m.link_image.startsWith('/media') ? `${BASE_URL}${m.link_image}` : m.link_image} style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }} alt="link_preview" />
                        ) : (
                            <div style={{ width: '100%', height: '120px', backgroundColor: '#e9f5ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ExternalLink size={28} color="#00a884" />
                            </div>
                        )}
                        <div style={{ backgroundColor: '#f0f2f5', padding: '12px 14px' }}>
                            <strong style={{ fontSize: '14.5px', color: '#111b21', display: 'block', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {m.link_title || activeLinkUrl}
                            </strong>
                            <span style={{ fontSize: '13px', color: '#00a884', display: 'block' }}>
                                {(() => { try { return new URL(activeLinkUrl).hostname; } catch { return activeLinkUrl; } })()}
                            </span>
                        </div>
                    </a>
                )}
            </div>
        );
    };

    const sidebarStyle = isMobile ? { display: selectedChat ? 'none' : 'flex', width: '100%', maxWidth: '100%' } : {};

    const mainStyle = isMobile 
        ? { display: (!selectedChat || rightPanelOpen) ? 'none' : 'flex', width: '100%', flex: 1, minWidth: 0, overflowX: 'hidden', flexDirection: 'column' } 
        : { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowX: 'hidden' };

    const rightPanelStyle = isMobile 
        ? { display: rightPanelOpen ? 'flex' : 'none', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 999999, background: '#f0f2f5', flexDirection: 'column' } 
        : { width: '300px', minWidth: '300px', borderLeft: '1px solid #e1e9eb', background: '#f0f2f5', display: 'flex', flexDirection: 'column', transition: '0.3s ease-in-out', flexShrink: 0 };

    return (
        <div className={`wa-chat-layout ${!sidebarOpen ? 'sidebar-closed' : ''}`} style={{ position: 'relative' }} onClick={() => { setShowActionMenu(false); setShowEmojiPicker(false); setShowTemplates(false); setContextMenu({ ...contextMenu, show: false }); setActiveMessageMenu(null); setShowInstanceMenu(false); }}>

            {mentionHover && (
                <div style={{
                    position: 'fixed', top: mentionHover.y, left: mentionHover.x, transform: 'translate(-50%, -100%)',
                    background: '#111b21', color: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 999999, display: 'flex', flexDirection: 'column', gap: '4px', pointerEvents: 'none', minWidth: '150px'
                }}>
                    <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{mentionHover.contact?.nome || mentionHover.phone}</div>
                    <div style={{ fontSize: '12px', color: '#8a9fac' }}>{mentionHover.contact?.whatsapp || mentionHover.phone}</div>
                    {mentionHover.contact?.email && <div style={{ fontSize: '11px', color: '#8a9fac' }}>{mentionHover.contact.email}</div>}

                    <div style={{
                        position: 'absolute', bottom: '-4px', left: '50%', transform: 'translateX(-50%)',
                        width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #111b21'
                    }} />
                </div>
            )}

            {showNewChatModal && (
                <div className="wa-overlay" style={{ zIndex: 999999 }}>
                    <div className="wa-modal-premium" style={{ maxWidth: '600px', width: '95%', height: isMobile ? '90vh' : '75vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div className="wa-modal-premium-header" style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <MessageSquare size={20} color="#00a884" />
                                <h3 style={{ margin: 0, color: '#111b21', fontSize: '18px' }}>Nova Conversa</h3>
                            </div>
                            <X size={24} onClick={() => setShowNewChatModal(false)} style={{ cursor: 'pointer', color: '#667781' }} />
                        </div>

                        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                            <button 
                                onClick={() => setNewChatTab('lista')}
                                style={{ flex: 1, padding: '14px', border: 'none', background: newChatTab === 'lista' ? '#fff' : 'transparent', color: newChatTab === 'lista' ? '#00a884' : '#64748b', fontWeight: 'bold', fontSize: '14px', borderBottom: newChatTab === 'lista' ? '3px solid #00a884' : '3px solid transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: '0.2s' }}
                            >
                                <List size={18} /> Meus Contatos
                            </button>
                            <button 
                                onClick={() => setNewChatTab('avulso')}
                                style={{ flex: 1, padding: '14px', border: 'none', background: newChatTab === 'avulso' ? '#fff' : 'transparent', color: newChatTab === 'avulso' ? '#00a884' : '#64748b', fontWeight: 'bold', fontSize: '14px', borderBottom: newChatTab === 'avulso' ? '3px solid #00a884' : '3px solid transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: '0.2s' }}
                            >
                                <UserPlus size={18} /> Número Avulso
                            </button>
                        </div>

                        <div className="wa-modal-premium-body custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#f8fafc' }}>
                            {newChatTab === 'lista' && (
                                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                                    {!contatoSelecionadoModal ? (
                                        <>
                                            <div style={{ position: 'relative', marginBottom: '15px' }}>
                                                <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                                                <input 
                                                    type="text" 
                                                    placeholder="Buscar contato (nome ou número)..." 
                                                    value={searchContatoModal}
                                                    onChange={(e) => setSearchContatoModal(e.target.value)}
                                                    style={{ width: '100%', padding: '12px 12px 12px 38px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box', fontSize: '14px' }}
                                                />
                                            </div>

                                            {loadingContatos ? (
                                                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#64748b' }}>Carregando contatos...</div>
                                            ) : contatosFiltradosModal.length === 0 ? (
                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                                                    <Users size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
                                                    <p style={{ margin: 0 }}>Nenhum contato individual encontrado.</p>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    {contatosFiltradosModal.map(contato => (
                                                        <div 
                                                            key={contato.id || contato.remote_jid_original || contato.whatsapp} 
                                                            onClick={() => setContatoSelecionadoModal(contato)}
                                                            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}
                                                            onMouseEnter={e => e.currentTarget.style.borderColor = '#00a884'}
                                                            onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                                                        >
                                                            <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#dfe5e7', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                {contato.profile_pic_url && contato.profile_pic_url !== 'no_pic' ? (
                                                                    <img src={contato.profile_pic_url} alt={contato.nome} referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                ) : (
                                                                    <User size={20} color="#fff" />
                                                                )}
                                                            </div>
                                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{contato.nome}</div>
                                                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{contato.whatsapp || contato.telefone || contato.remote_jid_original || 'Sem número cadastrado'}</div>
                                                            </div>
                                                            <div style={{ color: '#94a3b8', padding: '6px' }}>
                                                                <ChevronLeft size={18} style={{ transform: 'rotate(180deg)' }} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                            <button 
                                                onClick={() => setContatoSelecionadoModal(null)}
                                                style={{ background: 'none', border: 'none', padding: '0 0 15px 0', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', width: 'fit-content' }}
                                            >
                                                <ChevronLeft size={18} /> Voltar para a lista
                                            </button>

                                            <div style={{ textAlign: 'center', marginBottom: '25px', marginTop: '10px' }}>
                                                <div style={{ width: '90px', height: '90px', borderRadius: '50%', backgroundColor: '#dfe5e7', margin: '0 auto 15px auto', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '3px solid #fff', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}>
                                                    {contatoSelecionadoModal.profile_pic_url && contatoSelecionadoModal.profile_pic_url !== 'no_pic' ? (
                                                        <img src={contatoSelecionadoModal.profile_pic_url} referrerPolicy="no-referrer" alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <User size={45} color="#fff" />
                                                    )}
                                                </div>
                                                <h2 style={{ margin: '0 0 5px 0', color: '#0f172a', fontSize: '20px' }}>{contatoSelecionadoModal.nome}</h2>
                                                <p style={{ margin: 0, color: '#00a884', fontWeight: '600', fontSize: '15px' }}>{contatoSelecionadoModal.whatsapp || contatoSelecionadoModal.telefone || 'Sem número'}</p>
                                            </div>

                                            <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                                    <div>
                                                        <small style={{ color: '#94a3b8', display: 'block', fontSize: '12px', marginBottom: '2px' }}>E-mail</small>
                                                        <span style={{ color: '#334155', fontWeight: '500', fontSize: '13px', wordBreak: 'break-all' }}>{contatoSelecionadoModal.email || 'Não informado'}</span>
                                                    </div>
                                                    <div>
                                                        <small style={{ color: '#94a3b8', display: 'block', fontSize: '12px', marginBottom: '2px' }}>CPF</small>
                                                        <span style={{ color: '#334155', fontWeight: '500', fontSize: '13px' }}>{contatoSelecionadoModal.cpf || 'Não informado'}</span>
                                                    </div>
                                                    <div style={{ gridColumn: '1 / -1' }}>
                                                        <small style={{ color: '#94a3b8', display: 'block', fontSize: '12px', marginBottom: '2px' }}>Endereço</small>
                                                        <span style={{ color: '#334155', fontWeight: '500', fontSize: '13px' }}>
                                                            {contatoSelecionadoModal.endereco ? `${contatoSelecionadoModal.endereco}, ${contatoSelecionadoModal.numero || 'S/N'} - ${contatoSelecionadoModal.cidade}/${contatoSelecionadoModal.estado}` : 'Não informado'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '20px' }}>
                                                <button 
                                                    onClick={() => handleEditarDaAgenda(contatoSelecionadoModal)}
                                                    style={{ padding: '12px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: '0.2s' }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                                                >
                                                    <Edit3 size={18} /> Editar Cadastro
                                                </button>

                                                <button 
                                                    onClick={() => handleExcluirContatoAgenda(contatoSelecionadoModal)}
                                                    style={{ padding: '12px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: '0.2s' }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}
                                                >
                                                    <Trash2 size={18} /> Excluir Contato
                                                </button>
                                            </div>

                                            <button 
                                                onClick={() => handleIniciarChatPorContato(contatoSelecionadoModal)}
                                                style={{ width: '100%', padding: '16px', background: '#00a884', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(0,168,132,0.25)', marginTop: '10px' }}
                                            >
                                                <MessageSquare size={20} /> Iniciar Atendimento
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {newChatTab === 'avulso' && (
                                <form onSubmit={handleNewChatSubmitAvulso} style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
                                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                        <div style={{ background: '#e0f2fe', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px auto' }}>
                                            <Smartphone size={30} color="#0284c7" />
                                        </div>
                                        <h4 style={{ margin: '0 0 5px 0', color: '#0f172a', fontSize: '16px' }}>Iniciar conversa avulsa</h4>
                                        <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>Digite o número de WhatsApp para o qual deseja enviar uma mensagem.</p>
                                    </div>

                                    <div className="wa-input-group-premium" style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '8px', fontWeight: '600' }}>Número do WhatsApp (com DDD)</label>
                                        <input
                                            autoFocus
                                            type="text"
                                            value={newChatNumber}
                                            onChange={e => setNewChatNumber(e.target.value)}
                                            required
                                            placeholder="Ex: 11999999999"
                                            style={{ width: '100%', padding: '14px', borderRadius: '10px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box', fontSize: '16px', textAlign: 'center', letterSpacing: '1px' }}
                                        />
                                    </div>

                                    <button type="submit" className="wa-btn-save" style={{ padding: '14px', border: 'none', background: '#00a884', color: '#fff', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(0,168,132,0.2)' }}>
                                        <Send size={18} /> Iniciar Atendimento
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isMobile && (
                <button
                    className="wa-fab-new-chat"
                    onClick={(e) => {
                        if (isDraggingFab.current) {
                            e.preventDefault();
                            return;
                        }
                        setShowNewChatModal(true);
                    }}
                    onTouchMove={handleFabTouchMove}
                    onTouchEnd={handleFabTouchEnd}
                    style={{
                        position: 'fixed',
                        bottom: fabPos ? 'auto' : '20px',
                        right: fabPos ? 'auto' : '20px',
                        left: fabPos ? `${fabPos.x}px` : 'auto',
                        top: fabPos ? `${fabPos.y}px` : 'auto',
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: '#00a884',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        border: 'none',
                        zIndex: 99999,
                        touchAction: 'none'
                    }}
                    title="Iniciar Nova Conversa"
                >
                    <MessageSquare size={24} />
                </button>
            )}

            <Events 
                isOpen={showEventModal} 
                onClose={() => setShowEventModal(false)} 
                estabelecimentoId={estabelecimentoId}
                remoteJid={selectedChat?.remote_jid}
                nomeOperador={nomeOperador}
                idOperador={idOperador}
            />

            <div className="wa-sidebar" style={sidebarStyle}>
                <div className="wa-sidebar-header">
                    <div className="wa-profile-area">
                        <div className="wa-avatar-main">M</div>
                        {sidebarOpen && <span>Universo Mídia</span>}
                    </div>
                    <div className="wa-header-icons">
                        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="wa-icon-btn">
                            {sidebarOpen ? <ChevronLeft size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>

                <div className="wa-operator-config" style={{ display: 'flex', gap: '15px', justifyContent: sidebarOpen ? 'flex-start' : 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <label className="wa-switch">
                            <input type="checkbox" checked={exibirNomeOperador} onChange={() => setExibirNomeOperador(!exibirNomeOperador)} />
                            <span className="wa-slider"></span>
                        </label>
                        {sidebarOpen && <small>Nome do operador</small>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <label className="wa-switch">
                            <input type="checkbox" checked={permitirUpload} onChange={(e) => toggleUploadSite(e.target.checked)} />
                            <span className="wa-slider"></span>
                        </label>
                        {sidebarOpen && <small>Upload no Site</small>}
                    </div>
                </div>

                {sidebarOpen && (
                    <>
                        <div className="wa-search-bar" style={{ padding: '12px 14px', background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: '#f0f2f5', borderRadius: '10px', padding: '0 12px', height: '36px' }}>
                                    <Search size={16} color="#54656f" />
                                    <input 
                                        type="text" 
                                        placeholder="Buscar (nome, id, cpf...)" 
                                        value={searchTerm} 
                                        onChange={(e) => setSearchTerm(e.target.value)} 
                                        style={{ border: 'none', background: 'transparent', width: '100%', padding: '0 10px', outline: 'none', fontSize: '14px', color: '#111b21' }} 
                                    />
                                </div>
                                <button 
                                    onClick={() => setShowFilters(!showFilters)} 
                                    style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: showFilters ? '#d9fdd3' : 'transparent', color: showFilters ? '#00a884' : '#54656f', border: 'none', borderRadius: '50%', cursor: 'pointer', transition: 'all 0.2s ease' }}
                                    title="Filtros Avançados"
                                >
                                    <Filter size={18} />
                                </button>
                            </div>
                        </div>

                        {showFilters && (
                            <div style={{ padding: '8px 14px', background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', overflowX: 'auto' }} className="custom-scrollbar">
                                    <span 
                                        onClick={() => setSelectedTagFilter(null)} 
                                        style={{ flexShrink: 0, padding: '6px 14px', fontSize: '13px', borderRadius: '16px', cursor: 'pointer', background: selectedTagFilter === null ? '#00a884' : '#f0f2f5', color: selectedTagFilter === null ? '#fff' : '#54656f', transition: '0.2s' }}
                                    >
                                        Tudo
                                    </span>
                                    <span 
                                        onClick={() => setSelectedTagFilter('unread')} 
                                        style={{ flexShrink: 0, padding: '6px 14px', fontSize: '13px', borderRadius: '16px', cursor: 'pointer', background: selectedTagFilter === 'unread' ? '#00a884' : '#f0f2f5', color: selectedTagFilter === 'unread' ? '#fff' : '#54656f', transition: '0.2s' }}
                                    >
                                        Não lidas
                                    </span>
                                    <span 
                                        onClick={() => setSelectedTagFilter('favorites')} 
                                        style={{ flexShrink: 0, padding: '6px 14px', fontSize: '13px', borderRadius: '16px', cursor: 'pointer', background: selectedTagFilter === 'favorites' ? '#00a884' : '#f0f2f5', color: selectedTagFilter === 'favorites' ? '#fff' : '#54656f', transition: '0.2s' }}
                                    >
                                        Favoritos
                                    </span>
                                    {availableTags.map(tag => (
                                        <span 
                                            key={tag.id} 
                                            onClick={() => setSelectedTagFilter(tag.id)} 
                                            style={{ flexShrink: 0, padding: '6px 14px', fontSize: '13px', borderRadius: '16px', border: `1px solid ${selectedTagFilter === tag.id ? tag.cor : 'transparent'}`, background: selectedTagFilter === tag.id ? `${tag.cor}15` : '#f0f2f5', color: selectedTagFilter === tag.id ? tag.cor : '#54656f', cursor: 'pointer', transition: '0.2s', display: 'flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: tag.cor }} />
                                            {tag.nome}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="wa-tabs" style={{ display: 'flex', gap: '3px', padding: '10px' }}>
                            <button className={activeTab === 'whatsapp' ? 'active' : ''} onClick={() => setActiveTab('whatsapp')} style={{ flex: 1, display: 'flex', gap: '3px', justifyContent: 'center', position: 'relative', fontSize: '11px', padding: '8px 4px' }} title="WhatsApp">
                                <WhatsAppIcon size={14} /> WA
                                {unreadWhatsApp > 0 && <span style={{ background: '#ff3b30', color: '#fff', borderRadius: '10px', padding: '1px 4px', fontSize: '9px', fontWeight: 'bold', marginLeft: '2px' }}>{unreadWhatsApp}</span>}
                            </button>

                            <button className={activeTab === 'webchat' ? 'active' : ''} onClick={() => setActiveTab('webchat')} style={{ flex: 1, display: 'flex', gap: '3px', justifyContent: 'center', position: 'relative', fontSize: '11px', padding: '8px 4px' }} title="Webchat">
                                <WebchatIcon size={14} /> Site
                                {unreadWebchat > 0 && <span style={{ background: '#ff3b30', color: '#fff', borderRadius: '10px', padding: '1px 4px', fontSize: '9px', fontWeight: 'bold', marginLeft: '2px' }}>{unreadWebchat}</span>}
                            </button>

                            <button className={activeTab === 'grupos' ? 'active' : ''} onClick={() => setActiveTab('grupos')} style={{ flex: 1, display: 'flex', gap: '3px', justifyContent: 'center', position: 'relative', fontSize: '11px', padding: '8px 4px' }} title="Grupos">
                                <Users size={14} /> Grp
                                {unreadGrupos > 0 && <span style={{ background: '#ff3b30', color: '#fff', borderRadius: '10px', padding: '1px 4px', fontSize: '9px', fontWeight: 'bold', marginLeft: '2px' }}>{unreadGrupos}</span>}
                            </button>

                            <button className={activeTab === 'canais' ? 'active' : ''} onClick={() => setActiveTab('canais')} style={{ flex: 1, display: 'flex', gap: '3px', justifyContent: 'center', position: 'relative', fontSize: '11px', padding: '8px 4px' }} title="Canais / Atualizações">
                                <Megaphone size={14} /> Canais
                                {unreadCanais > 0 && <span style={{ background: '#ff3b30', color: '#fff', borderRadius: '10px', padding: '1px 4px', fontSize: '9px', fontWeight: 'bold', marginLeft: '2px' }}>{unreadCanais}</span>}
                            </button>
                        </div>
                    </>
                )}

                <div className="wa-chat-list custom-scrollbar" style={{ position: 'relative' }}>
                    {conversasFiltradas.map((chat) => {
                        const isEvent = chat.type === 'event';
                        const currentEvType = chat.eventType || chat.event_type || 'neutral';
                        const isTagEvent = currentEvType === 'tag_add' || currentEvType === 'tag_remove';
                        const isWeb = chat.isWebChat === true || chat.channel === 'webchat' || String(chat.remote_jid).startsWith('web_');

                        let EvIcon = Target;
                        let evColor = '#8a9fac';
                        let evLabel = 'Neutro';

                        if (isTagEvent) {
                            EvIcon = Tag;
                            evColor = chat.tag_color || chat.tagColor || '#334155';
                            evLabel = currentEvType === 'tag_add' ? 'Adicionou Etiqueta' : 'Removeu Etiqueta';
                        } else if (currentEvType === 'line_swap') {
                            EvIcon = ArrowRightLeft; evColor = '#3b82f6'; evLabel = 'Troca de Linha';
                        } else if (currentEvType === 'positive') {
                            EvIcon = CheckCircle; evColor = '#25d366'; evLabel = 'Positivo';
                        } else if (currentEvType === 'negative') {
                            EvIcon = XCircle; evColor = '#ea1d2c'; evLabel = 'Negativo';
                        }

                        return (
                            <div key={chat.remote_jid} className={`wa-chat-item ${selectedChat?.remote_jid === chat.remote_jid ? 'active' : ''}`} onClick={() => carregarHistorico(chat)} onContextMenu={(e) => handleContextMenu(e, chat)}>

                                <div style={{ position: 'relative', width: '48px', height: '48px', flexShrink: 0, marginRight: '15px' }}>
                                    <div 
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            if (chat.profilePicUrl && chat.profilePicUrl !== 'no_pic') setPreviewImage(chat.profilePicUrl); 
                                        }}
                                        style={{ width: '100%', height: '100%', borderRadius: '50%', backgroundColor: '#dfe5e7', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: chat.profilePicUrl && chat.profilePicUrl !== 'no_pic' ? 'pointer' : 'default' }}
                                    >
                                        {chat.profilePicUrl && chat.profilePicUrl !== 'no_pic' ? (
                                            <img src={chat.profilePicUrl} alt="avatar" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <span style={{ fontSize: '20px', color: '#fff', fontWeight: '500' }}>
                                                {chat.contactName?.charAt(0).toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    {isWeb && (
                                        <div 
                                            style={{ 
                                                position: 'absolute', bottom: 0, right: 0, width: '13px', height: '13px', 
                                                borderRadius: '50%', border: '2px solid #fff', 
                                                backgroundColor: isWebchatOnline(chat) ? '#25d366' : '#94a3b8' 
                                            }} 
                                            title={isWebchatOnline(chat) ? 'Online no Site' : 'Offline'}
                                        />
                                    )}
                                </div>

                                {sidebarOpen && (
                                    <div className="wa-chat-content" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <div className="wa-chat-info-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                            <span className="wa-chat-name" style={{ fontWeight: chat.unreadCount > 0 ? '600' : '400', fontSize: '15px', color: '#111b21', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                {chat.is_favorite && <Pin size={14} fill="#ffb02e" color="#ffb02e" />}
                                                {chat.chat_type === 'group' && <Users size={14} color="#64748b" />}
                                                {chat.chat_type === 'newsletter' && <Megaphone size={14} color="#64748b" />}
                                                {chat.contactName}
                                            </span>
                                            <span className="wa-chat-time-sidebar" style={{ color: chat.unreadCount > 0 ? '#25d366' : '#667781' }}>{chat.timestamp ? new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                        </div>
                                        <div className="wa-chat-info-row secondary" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                                            <div className="wa-chat-preview" style={{ fontWeight: chat.unreadCount > 0 ? '500' : '400', color: chat.unreadCount > 0 ? '#111b21' : '#667781', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%', display: 'flex', alignItems: 'center' }}>
                                                {isEvent ? (
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic', display: 'flex', alignItems: 'center', color: isTagEvent ? '#64748b' : evColor }}>
                                                        <EvIcon size={14} color={evColor} style={{ marginRight: 4, flexShrink: 0, transform: isTagEvent ? 'rotate(90deg)' : 'none' }} />
                                                        {isTagEvent || currentEvType === 'line_swap' ? chat.lastMsg : `Evento (${evLabel}): ${chat.lastMsg}`}
                                                    </span>
                                                ) : (
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                                                        {chat.from_me ? <CheckCheck size={14} color={chat.status === 'READ' || chat.status === 4 ? '#53bdeb' : '#8696a0'} style={{ marginRight: 4, flexShrink: 0 }} /> : ''}
                                                        Última msg: {chat.lastMsg || "Mídia"}
                                                    </span>
                                                )}
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                {chat.unreadCount > 0 && (
                                                    <span style={{ background: '#25d366', color: '#fff', borderRadius: '50%', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}>
                                                        {chat.unreadCount}
                                                    </span>
                                                )}
                                                <div className="wa-chat-tags-mini" style={{ display: 'flex', gap: '4px' }}>
                                                    {chat.tags?.map(t => <div key={t.id} style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: t.cor }} title={t.nome} />)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="wa-main" style={mainStyle}>
                {selectedChat ? (
                    <>
                        <div className="wa-main-header" style={{ width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '8px 4px' : '10px 20px', gap: '5px' }}>
                            <div className="wa-contact-info" style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                {isMobile && (
                                    <button 
                                        onClick={() => setSelectedChat(null)}
                                        style={{ background: 'none', border: 'none', padding: '8px 12px 8px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                    >
                                        <ChevronLeft size={28} color="#54656f" />
                                    </button>
                                )}

                                <div 
                                    onClick={() => { if (selectedChat.profilePicUrl && selectedChat.profilePicUrl !== 'no_pic') setPreviewImage(selectedChat.profilePicUrl); }}
                                    style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#dfe5e7', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, marginRight: '12px', cursor: selectedChat.profilePicUrl && selectedChat.profilePicUrl !== 'no_pic' ? 'pointer' : 'default' }}
                                >
                                    {selectedChat.profilePicUrl && selectedChat.profilePicUrl !== 'no_pic' ? (
                                        <img src={selectedChat.profilePicUrl} alt="avatar" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <span style={{ fontSize: '18px', color: '#fff', fontWeight: '500' }}>
                                            {selectedChat.contactName?.charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <div className="wa-contact-text" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                        <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '15px' }}>
                                            {selectedChat.chat_type === 'group' && <span style={{ background: '#f1f5f9', color: '#475569', fontSize: '11px', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '4px' }}><Users size={12}/> Grupo</span>}
                                            {selectedChat.chat_type === 'newsletter' && <span style={{ background: '#f1f5f9', color: '#475569', fontSize: '11px', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '4px' }}><Megaphone size={12}/> Canal</span>}

                                            {selectedChat.contactName}

                                            {selectedChat.remote_jid?.startsWith('web_') && (
                                                <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '10px', backgroundColor: isWebchatOnline(selectedChat) ? '#dcfce7' : '#f1f5f9', color: isWebchatOnline(selectedChat) ? '#16a34a' : '#64748b', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}>
                                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isWebchatOnline(selectedChat) ? '#16a34a' : '#94a3b8' }}></div>
                                                    {isWebchatOnline(selectedChat) ? 'Online no Site' : 'Offline'}
                                                </span>
                                            )}

                                            {selectedChat.is_favorite && <Pin size={14} fill="#ffb02e" color="#ffb02e" />}
                                        </h4>

                                        {!isMobile && (
                                            <button 
                                                className={`wa-channel-pill ${currentChannel}`} 
                                                onClick={() => {
                                                    if (isWebchatEnabled) {
                                                        setCurrentChannel(currentChannel === 'webchat' ? 'whatsapp' : 'webchat');
                                                    } else {
                                                        setCurrentChannel('whatsapp');
                                                    }
                                                }} 
                                                title={isWebchatEnabled ? `Clique para responder via ${currentChannel === 'webchat' ? 'WhatsApp' : 'Webchat'}` : "Webchat desabilitado"}
                                                style={{ flexShrink: 0 }}
                                            >
                                                {currentChannel === 'webchat' ? <WebchatIcon size={12} /> : <WhatsAppIcon size={12} />}
                                                {currentChannel === 'webchat' ? 'Webchat' : 'WhatsApp'}
                                            </button>
                                        )}

                                        {currentChannel === 'webchat' && !isMobile && (
                                            <button
                                                onClick={() => toggleUploadSite(!permitirUpload)}
                                                title={permitirUpload ? "Bloquear envio de arquivos pelo cliente" : "Permitir envio de arquivos pelo cliente"}
                                                style={{ 
                                                    background: 'none', border: 'none', cursor: 'pointer', padding: '5px',
                                                    color: permitirUpload ? '#10b981' : '#ef4444', display: 'flex', alignItems: 'center', flexShrink: 0
                                                }}
                                            >
                                                {permitirUpload ? <Unlock size={16} /> : <Lock size={16} />}
                                            </button>
                                        )}
                                    </div>
                                    <span className="wa-contact-number" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', fontSize: '12px' }}>
                                        {selectedChat.remote_jid?.startsWith('web_') ? (contactData.whatsapp ? `Site • ${contactData.whatsapp}` : 'Visitante Site') : cleanJid(selectedChat.remote_jid)}
                                    </span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: isMobile ? '2px' : '8px' }}>
                                {currentChannel === 'whatsapp' &&
                                    instances.length > 0 &&
                                    !isSelectedChatGroup && (
                                    <div style={{ position: 'relative' }}>
                                        <div 
                                            onClick={(e) => { e.stopPropagation(); setShowInstanceMenu(!showInstanceMenu); }}
                                            style={{ 
                                                display: 'flex', alignItems: 'center', gap: '4px', 
                                                background: '#f8fafc', padding: isMobile ? '4px 6px' : '6px 12px', 
                                                borderRadius: '8px', border: '1px solid #cbd5e1', 
                                                cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                maxWidth: isMobile ? '110px' : '200px', justifyContent: 'space-between'
                                            }}
                                            title="Escolher por qual número de WhatsApp enviar"
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
                                                <Smartphone size={isMobile ? 12 : 14} color="#00a884" style={{ flexShrink: 0 }} />
                                                <span style={{ fontSize: isMobile ? '10px' : '12px', fontWeight: '600', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {instances.find(i => i.id === selectedInstanceId)?.name || 'Linha...'}
                                                </span>
                                            </div>
                                            <ChevronDown size={isMobile ? 12 : 14} color="#64748b" style={{ flexShrink: 0, transform: showInstanceMenu ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                                        </div>

                                        {showInstanceMenu && (
                                            <div 
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ 
                                                    position: 'absolute', top: '100%', right: 0, marginTop: '4px', 
                                                    background: '#fff', border: '1px solid #e2e8f0', 
                                                    borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', 
                                                    zIndex: 100, minWidth: '180px', overflow: 'hidden'
                                                }}
                                            >
                                                <div style={{ padding: '8px 12px', background: '#f1f5f9', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Enviar mensagem por:
                                                </div>
                                                {instances.map(inst => (
                                                    <div 
                                                        key={inst.id} 
                                                        onClick={() => handleInstanceChange(inst.id)}
                                                        style={{ 
                                                            padding: '10px 12px', fontSize: '13px', color: '#1e293b', 
                                                            cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                            background: selectedInstanceId === inst.id ? '#f0fdf4' : '#fff',
                                                            fontWeight: selectedInstanceId === inst.id ? '600' : '400'
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.background = selectedInstanceId === inst.id ? '#f0fdf4' : '#f8fafc'}
                                                        onMouseLeave={e => e.currentTarget.style.background = selectedInstanceId === inst.id ? '#f0fdf4' : '#fff'}
                                                    >
                                                        {inst.name}
                                                        {selectedInstanceId === inst.id && <CheckCircle size={14} color="#16a34a" />}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <button 
                                    onClick={() => setRightPanelOpen(!rightPanelOpen)} 
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: isMobile ? '4px 2px' : '8px', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center' }}
                                    title="Detalhes do Cliente"
                                >
                                    <PanelRight size={isMobile ? 22 : 24} color={rightPanelOpen ? "#00a884" : "#54656f"} />
                                </button>
                            </div>
                        </div>

                        {showChatLineBadge && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: '8px 12px 0',
                                    background: 'transparent',
                                    position: 'relative',
                                    zIndex: 2
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '5px 12px',
                                        borderRadius: '8px',
                                        background: 'rgba(255,255,255,0.90)',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        fontSize: '12px',
                                        color: '#475569',
                                        fontWeight: '500'
                                    }}
                                >
                                    {isSelectedChatNewsletter ? (
                                        <Megaphone size={13} color="#0284c7" />
                                    ) : (
                                        <Users size={13} color="#16a34a" />
                                    )}

                                    <span>{isSelectedChatNewsletter ? 'Canal' : 'Grupo'}</span>
                                    <span style={{ color: '#94a3b8' }}>•</span>
                                    <span>Linha:</span>
                                    <strong style={{ color: '#0f172a' }}>{currentInstance?.name || 'Não identificada'}</strong>

                                    {currentInstance?.id && (
                                        <span style={{ color: '#64748b' }}>({currentInstance.id})</span>
                                    )}
                                </div>
                            </div>
                        )}

                        <div 
                            className="wa-chat-bg custom-scrollbar" 
                            style={{ overflowY: 'auto', overflowX: 'hidden', padding: isChannelChat ? '20px 0' : '10px 20px' }}
                            onMouseOver={handleMentionHover}
                            onMouseOut={handleMentionLeave}
                            onClick={handleMentionClick}
                        >
                            {mensagens.map((m, i) => {
                                const prevMsg = i > 0 ? mensagens[i - 1] : null;
                                const currentIsWeb = m.isWebChat === true || m.channel === 'webchat' || String(m.remote_jid).startsWith('web_');
                                const prevIsWeb = prevMsg ? (prevMsg.isWebChat === true || prevMsg.channel === 'webchat' || String(prevMsg.remote_jid).startsWith('web_')) : null;
                                const channelChanged = prevMsg !== null && currentIsWeb !== prevIsWeb;

                                const nomeCanalAtual = currentIsWeb ? 'Webchat' : 'WhatsApp';
                                const nomeCanalAnterior = prevIsWeb ? 'Webchat' : 'WhatsApp';

                                const isSystemLog = m.type === 'system' || String(m.text || '').startsWith('Canal alterado') || String(m.text || '').startsWith('🔄');

                                if (m.type === 'event') {
                                    const currentEventType = m.eventType || m.event_type || 'neutral';
                                    const nomeOperadorExibicao = m.push_name || m.pushName || 'Sistema';
                                    const timeStr = new Date(m.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                                    if (currentEventType === 'line_swap') {
                                        return (
                                            <React.Fragment key={m.msg_id || i}>
                                                <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0' }}>
                                                    <span style={{ 
                                                        background: '#f1f5f9', color: '#475569', fontSize: '11.5px', padding: '6px 14px', 
                                                        borderRadius: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px',
                                                        border: '1px dashed #cbd5e1'
                                                    }}>
                                                        <ArrowRightLeft size={14} color="#3b82f6" />
                                                        {m.text || m.content}
                                                        <span style={{ color: '#94a3b8', fontSize: '10px', marginLeft: '4px' }}>por {nomeOperadorExibicao} - {timeStr}</span>
                                                    </span>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }

                                    if (currentEventType === 'tag_add' || currentEventType === 'tag_remove') {
                                        const corTag = m.tag_color || m.tagColor || (currentEventType === 'tag_add' ? '#00a884' : '#ef4444');
                                        const isAdd = currentEventType === 'tag_add';
                                        const actionText = m.text || m.content || (isAdd ? 'Etiqueta adicionada' : 'Etiqueta removida');

                                        return (
                                            <React.Fragment key={m.msg_id || i}>
                                                <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                                                    <div style={{ 
                                                        display: 'flex', alignItems: 'center', gap: '8px', 
                                                        background: '#fff', border: '1px solid #e1e9eb', padding: '6px 14px', 
                                                        borderRadius: '24px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                        fontSize: '12.5px', color: '#111b21'
                                                    }}>
                                                        <Tag size={16} color={corTag} fill={corTag} style={{ transform: 'rotate(90deg)' }} />
                                                        <span>
                                                            {actionText.split(' por ')[0]} 
                                                            <span style={{ color: '#54656f' }}> por <strong>{nomeOperadorExibicao}</strong> - {timeStr}</span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }

                                    const EventIcon = currentEventType === 'positive' ? CheckCircle : currentEventType === 'negative' ? XCircle : Target;
                                    const eventColor = currentEventType === 'positive' ? '#25d366' : currentEventType === 'negative' ? '#ea1d2c' : '#8a9fac';
                                    const textoEvento = m.text || m.content || 'Evento sem descrição';

                                    return (
                                        <React.Fragment key={m.msg_id || i}>
                                            <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0' }}>
                                                <span style={{ color: '#667781', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: 'transparent', padding: '2px 8px', borderRadius: '8px' }}>
                                                    <EventIcon size={14} color={eventColor} />
                                                    {textoEvento}, adicionado por <strong>{nomeOperadorExibicao}</strong> - {timeStr}
                                                </span>
                                            </div>
                                        </React.Fragment>
                                    );
                                }

                                if (isSystemLog) {
                                    return (
                                        <React.Fragment key={m.msg_id || i}>
                                            {channelChanged && (
                                                <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0' }}>
                                                    <span style={{ background: '#e9edef', color: '#555', fontSize: '11px', padding: '5px 14px', borderRadius: '12px', fontWeight: '500', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <MessageSquare size={13} color="#00a884" /> Canal alterado &lt;{nomeCanalAnterior} &gt; {nomeCanalAtual}
                                                    </span>
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                                                <div style={{ background: '#f0f2f5', border: '1px solid #e1e9eb', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', color: '#54656f', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                                                    <MessageSquare size={14} color="#00a884" />
                                                    <span>{m.text} - {new Date(m.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                }

                                if (isChannelChat) {
                                    const mediaPath = m.mediaUrl || m.media_url || m.url || m.fileUrl || m.documentUrl;
                                    const url = mediaPath ? (mediaPath.startsWith('http') || mediaPath.startsWith('blob:') || mediaPath.startsWith('data:') ? mediaPath : `${BASE_URL}${mediaPath}`) : null;
                                    const textContent = m.text || m.content || "";
                                    const type = String(m.type || '').toLowerCase();

                                    const isImage = type.includes('image') || (url && url.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i));
                                    const isVideo = type.includes('video') || (url && url.match(/\.(mp4|mov|avi)($|\?)/i));
                                    const isAudio = type.includes('audio') || type.includes('ptt') || type.includes('audiomessage') || (url && url.match(/\.(ogg|mp3|wav|m4a|opus)($|\?)/i));

                                    const textIsFilename = textContent && textContent.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|txt|csv)$/i);
                                    const typeIsDoc = type.includes('document') || type.includes('file');
                                    const urlIsDoc = url && url.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|txt|csv)($|\?)/i);

                                    const isDoc = (typeIsDoc || urlIsDoc || textIsFilename || (url && !isImage && !isVideo && !isAudio)) && !isAudio;

                                    const downloadFileUrl = url || (extractUrlFromText(textContent));
                                    const displayFileName = textIsFilename ? textContent : getFileNameFromUrl(downloadFileUrl || url);
                                    const textToRender = (isDoc && (textContent === displayFileName || textContent === downloadFileUrl)) ? "" : textContent;

                                    const activeLinkUrl = m.link_url || extractUrlFromText(textToRender);
                                    const msgUniqueId = m.msg_id || m.key?.id || i;

                                    return (
                                        <React.Fragment key={msgUniqueId}>
                                            {channelChanged && (
                                                <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0' }}>
                                                    <span style={{ background: '#e9edef', color: '#555', fontSize: '11px', padding: '5px 14px', borderRadius: '12px', fontWeight: '500', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <MessageSquare size={13} color="#00a884" /> Canal alterado &lt;{nomeCanalAnterior} &gt; {nomeCanalAtual}
                                                    </span>
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 0 20px 0', width: '100%' }}>
                                                <div style={{ width: '92%', maxWidth: '560px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e1e9eb', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', position: 'relative' }}>
                                                    <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 20 }}>
                                                        <button onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            if (activeMessageMenu?.id === msgUniqueId) { setActiveMessageMenu(null); } 
                                                            else {
                                                                const menuHeight = 180; const spaceBelow = window.innerHeight - rect.bottom;
                                                                const y = spaceBelow < menuHeight ? rect.top - menuHeight - 4 : rect.bottom + 4;
                                                                const x = rect.right - 160;
                                                                setActiveMessageMenu({ id: msgUniqueId, msg: m, x: Math.max(10, x), y });
                                                            }
                                                        }} style={{ background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}>
                                                            <ChevronDown size={14} color="#54656f" />
                                                        </button>
                                                    </div>

                                                    {(isImage || isVideo) && url && (
                                                        <div style={{ position: 'relative', width: '100%', backgroundColor: '#e9edef', cursor: 'pointer' }} onClick={() => setPreviewImage(url)}>
                                                            {isImage ? (
                                                                <img src={url} alt="media" style={{ width: '100%', maxHeight: '420px', objectFit: 'cover', display: 'block' }} />
                                                            ) : (
                                                                <video src={url} style={{ width: '100%', maxHeight: '420px', objectFit: 'cover', display: 'block' }} controls />
                                                            )}
                                                            <div style={{ position: 'absolute', bottom: '12px', left: '12px', background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '6px 12px', borderRadius: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', backdropFilter: 'blur(4px)' }}>
                                                                <Download size={14} /> {isImage ? '94 kB' : 'Vídeo'}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {isDoc && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f0f2f5', padding: '12px', borderBottom: '1px solid #e1e9eb' }}>
                                                            <div style={{ background: '#00a884', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                <FileText size={24} color="#fff" />
                                                            </div>
                                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                                <strong style={{ fontSize: '14px', color: '#111b21', display: 'block', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }} title={displayFileName}>
                                                                    {displayFileName}
                                                                </strong>
                                                            </div>
                                                            {downloadFileUrl ? (
                                                                <a 
                                                                    href={downloadFileUrl} 
                                                                    download={displayFileName} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer" 
                                                                    style={{ color: '#54656f', display: 'flex', alignItems: 'center', padding: '6px' }}
                                                                    title="Baixar arquivo"
                                                                >
                                                                    <Download size={20} />
                                                                </a>
                                                            ) : (
                                                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>S/ Link</span>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div style={{ padding: '12px 16px 10px 16px' }}>
                                                        {textToRender && (
                                                            <p style={{ fontSize: '15.5px', color: '#111b21', margin: '0 0 10px 0', lineHeight: '1.4', wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatarTextoWhatsApp(textToRender), { ADD_ATTR: ['target', 'rel', 'data-mention'] }) }} />
                                                        )}
                                                        {activeLinkUrl && (
                                                            <a href={activeLinkUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none', border: '1px solid #e1e9eb', borderRadius: '10px', overflow: 'hidden', marginBottom: '4px' }}>
                                                                {m.link_image ? (
                                                                    <img src={m.link_image.startsWith('http') || m.link_image.startsWith('/media') ? `${BASE_URL}${m.link_image}` : m.link_image} style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }} alt="link_preview" />
                                                                ) : (
                                                                    <div style={{ width: '100%', height: '120px', backgroundColor: '#e9f5ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <ExternalLink size={28} color="#00a884" />
                                                                    </div>
                                                                )}
                                                                <div style={{ backgroundColor: '#f0f2f5', padding: '12px 14px' }}>
                                                                    <strong style={{ fontSize: '14.5px', color: '#111b21', display: 'block', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                        {m.link_title || activeLinkUrl}
                                                                    </strong>
                                                                    <span style={{ fontSize: '13px', color: '#00a884', display: 'block' }}>
                                                                        {(() => { try { return new URL(activeLinkUrl).hostname; } catch { return activeLinkUrl; } })()}
                                                                    </span>
                                                                </div>
                                                            </a>
                                                        )}
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2px' }}>
                                                            <span style={{ fontSize: '11px', color: '#667781' }}>{new Date(m.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ width: '92%', maxWidth: '560px', display: 'flex', gap: '8px', marginTop: '6px' }}>
                                                    {m.reaction && (
                                                        <div style={{ background: '#fff', border: '1px solid #e1e9eb', borderRadius: '16px', padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                                            <span style={{ fontSize: '16px' }}>{m.reaction}</span>
                                                        </div>
                                                    )}
                                                    {!isChannelChat && (
                                                        <div style={{ background: '#fff', border: '1px solid #e1e9eb', borderRadius: '16px', padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }} onClick={(e) => { e.stopPropagation(); setShowEmojiPicker(!showEmojiPicker); }}>
                                                            <span style={{ fontSize: '13px', color: '#54656f', fontWeight: '500' }}>+ Reagir</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                }

                                return (
                                    <React.Fragment key={m.msg_id || i}>
                                        {channelChanged && (
                                            <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0' }}>
                                                <span style={{ background: '#e9edef', color: '#555', fontSize: '11px', padding: '5px 14px', borderRadius: '12px', fontWeight: '500', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <MessageSquare size={13} color="#00a884" /> Canal alterado &lt;{nomeCanalAnterior} &gt; {nomeCanalAtual}
                                                </span>
                                            </div>
                                        )}
                                        <div id={`msg-${m.msg_id}`} className={`wa-message-row ${m.from_me ? 'sent' : 'received'}`} style={{ transition: 'background-color 0.5s ease', marginBottom: m.reaction ? '16px' : '4px' }}>
                                            <div className="wa-bubble" style={{ position: 'relative' }}>
                                                {renderMessageContentNormal(m, i)}
                                                <div className="wa-time" style={{ display: 'flex', alignItems: 'center', gap: '3px', justifyContent: 'flex-end' }}>
                                                    {new Date(m.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    {m.from_me === 1 && renderMessageStatus(
                                                        m.statusCode ?? m.status_code ?? m.status ?? m.ack,
                                                        14
                                                    )}
                                                </div>

                                                {m.reaction && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        bottom: '-12px',
                                                        right: '5px',
                                                        background: '#fff',
                                                        border: '1px solid #e1e9eb',
                                                        borderRadius: '16px',
                                                        padding: '2px 4px',
                                                        fontSize: '13px',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                                        zIndex: 10,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: '22px',
                                                        height: '22px',
                                                        lineHeight: '1'
                                                    }}>
                                                        {m.reaction}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {selectedChat?.remote_jid?.startsWith('web_') && !isWebchatOnline(selectedChat) && (
                            <div style={{ background: '#fef2f2', color: '#ef4444', padding: '10px 15px', fontSize: '13px', textAlign: 'center', borderTop: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontWeight: '500' }}>
                                <XCircle size={16} /> Este visitante fechou o site ou está offline. Ele não receberá sua mensagem.
                            </div>
                        )}

                        <div className="wa-footer" style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                            {showEmojiPicker && <div className="wa-emoji-picker-container" onClick={(e) => e.stopPropagation()}><EmojiPicker onEmojiClick={onEmojiClick} theme="light" /></div>}

                            {replyingTo && (
                                <div style={{ 
                                    background: '#f0f2f5', padding: '8px 12px', borderLeft: '4px solid #00a884', display: 'flex', 
                                    justifyContent: 'space-between', alignItems: 'center', margin: '0 15px 8px 15px', borderRadius: '6px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                                }}>
                                    <div style={{ overflow: 'hidden' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#00a884', display: 'block', marginBottom: '2px' }}>
                                            Respondendo a {getQuotedName(replyingTo.from_me ? 'Você' : selectedChat?.remote_jid)}
                                        </span>
                                        <span style={{ fontSize: '12px', color: '#54656f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                            {replyingTo.text || replyingTo.content || 'Mídia / Outro Formato'}
                                        </span>
                                    </div>
                                    <button type="button" onClick={() => setReplyingTo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#667781', display: 'flex', padding: '4px' }}>
                                        <X size={16} />
                                    </button>
                                </div>
                            )}

                            {showTemplates && (
                                <div className="wa-templates-popup custom-scrollbar" onClick={(e) => e.stopPropagation()} style={{ 
                                    position: 'absolute', bottom: '100%', left: '15px', background: '#fff', border: '1px solid #e1e9eb', 
                                    borderRadius: '8px', width: '350px', maxHeight: '320px', overflowY: 'auto', zIndex: 1000, 
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)' 
                                }}>
                                    <div style={{ padding: '12px 15px', borderBottom: '1px solid #e1e9eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Zap size={16} color="#ea1d2c" />
                                            <h4 style={{ margin: 0, fontSize: '14px', color: '#111b21', fontWeight: 'bold' }}>Respostas Rápidas</h4>
                                        </div>
                                        <X size={16} onClick={() => setShowTemplates(false)} style={{ cursor: 'pointer', color: '#667781' }} />
                                    </div>
                                    {templates.length > 0 ? (
                                        <div style={{ padding: '6px' }}>
                                            {templates.map(t => (
                                                <div 
                                                    key={t.id} 
                                                    style={{ 
                                                        padding: '10px 12px', borderBottom: '1px solid #f1f5f9', borderRadius: '6px',
                                                        display: 'flex', flexDirection: 'column', gap: '4px', transition: 'background 0.15s'
                                                    }} 
                                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} 
                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <strong style={{ fontSize: '13px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            {t.NomeTemplate || "Template"}
                                                            {t.TipoArquivo === 'imagem' && <span style={{ fontSize: '10px', color: '#2563eb' }}>🖼️</span>}
                                                            {t.TipoArquivo === 'video' && <span style={{ fontSize: '10px', color: '#ea1d2c' }}>🎥</span>}
                                                        </strong>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSelectTemplate(t)}
                                                                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}
                                                                title="Inserir texto no editor"
                                                            >
                                                                Editar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSendTemplateDirect(t)}
                                                                style={{ background: '#ea1d2c', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: '700', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                                                                title="Enviar agora"
                                                            >
                                                                <Send size={10} /> Enviar
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <span style={{ fontSize: '12px', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.4' }}>
                                                        {processarVariaveis(t.Descricao)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ padding: '24px', textAlign: 'center', color: '#667781', fontSize: '13px' }}>
                                            Nenhum template cadastrado.
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="wa-input-container" style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: '10px', padding: '10px 15px' }}>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                    <button 
                                        type="button" 
                                        className="wa-icon-btn" 
                                        onClick={(e) => { e.stopPropagation(); setShowActionMenu(!showActionMenu); setShowEmojiPicker(false); setShowTemplates(false); }}
                                        style={{ background: showActionMenu ? '#e9edef' : 'transparent', borderRadius: '50%' }}
                                    >
                                        <Plus size={24} style={{ transform: showActionMenu ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s ease-in-out' }} />
                                    </button>

                                    {showActionMenu && (
                                        <div 
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                position: 'absolute', bottom: '100%', left: '0', marginBottom: '15px',
                                                display: 'flex', flexDirection: 'column', gap: '4px',
                                                background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(12px)',
                                                padding: '10px', borderRadius: '16px',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid rgba(255,255,255,0.8)',
                                                minWidth: '220px', zIndex: 999
                                            }}
                                        >
                                            <ActionMenuButton icon={Camera} label="Câmera (Foto/Vídeo)" onClick={() => { cameraInputRef.current?.click(); setShowActionMenu(false); }} color="#3b82f6" />
                                            {(currentChannel === 'whatsapp' || (currentChannel === 'webchat' && permitirUpload)) && (
                                                <ActionMenuButton icon={Paperclip} label="Documento ou Arquivo" onClick={() => { fileInputRef.current?.click(); setShowActionMenu(false); }} color="#8a9fac" />
                                            )}
                                            <ActionMenuButton icon={Zap} label="Respostas Rápidas" onClick={() => { setShowTemplates(true); setShowActionMenu(false); }} color="#ea1d2c" />
                                            <ActionMenuButton icon={Tag} label="Etiquetas do Cliente" onClick={() => { setShowTagManager(true); setShowActionMenu(false); }} color="#00a884" />
                                            <ActionMenuButton icon={Target} label="Aplicar Evento" onClick={() => { setShowEventModal(true); setShowActionMenu(false); }} color="#00a5ff" />
                                        </div>
                                    )}
                                </div>

                                {isRecording ? (
                                    <div className="wa-recording-wrapper" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px' }}>
                                        <div className="wa-recording-display"><div className="wa-icon-btn mic-btn recording"></div><span className="wa-recording-timer">{formatTime(recordingTime)}</span></div>
                                        <button 
                                            type="button" 
                                            className="wa-icon-btn mic-btn recording" 
                                            onPointerDown={handleStartRecord} 
                                            onPointerUp={handleStopRecord}
                                            onPointerCancel={handleStopRecord}
                                            onContextMenu={(e) => e.preventDefault()}
                                            style={{ touchAction: 'none' }}
                                        >
                                            <Mic size={22} />
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSend} className="wa-form-input" style={{ flex: 1, display: 'flex', alignItems: 'flex-end', background: '#fff', borderRadius: '20px', padding: '8px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px 4px 0', color: '#54656f' }} onClick={(e) => { e.stopPropagation(); setShowEmojiPicker(!showEmojiPicker); setShowActionMenu(false); setShowTemplates(false); }}>
                                            <Smile size={24} />
                                        </button>
                                        <textarea ref={textareaRef} value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder={isRecording ? "" : "Digite uma mensagem"} disabled={isRecording} rows={1} className="wa-textarea" style={{ flex: 1, border: 'none', background: 'transparent', resize: 'none', outline: 'none', padding: '4px', fontFamily: 'inherit', fontSize: '15px', maxHeight: '100px' }} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); } }} />
                                        {newMessage.trim() === "" && !fileToUpload ? (
                                            <button 
                                                type="button" 
                                                className={`wa-icon-btn mic-btn ${isRecording ? 'recording' : ''}`} 
                                                style={{ padding: '0 0 4px 8px', color: '#54656f', background: 'none', border: 'none', cursor: 'pointer', touchAction: 'none' }} 
                                                onPointerDown={handleStartRecord} 
                                                onPointerUp={handleStopRecord}
                                                onPointerCancel={handleStopRecord}
                                                onContextMenu={(e) => e.preventDefault()}
                                            >
                                                <Mic size={24} />
                                            </button>
                                        ) : (
                                            <button type="submit" className="wa-send-btn" style={{ background: '#00a884', color: '#fff', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: '8px' }}><Send size={18} style={{ transform: 'translateX(1px)' }} /></button>
                                        )}
                                    </form>
                                )}
                                <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
                                <input type="file" ref={cameraInputRef} accept="image/*,video/*" capture="environment" style={{ display: 'none' }} onChange={handleFileSelect} />
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="wa-welcome" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', position: 'relative' }}>
                        <ShoppingBag size={64} color="#d1d7db" />
                        <h1 style={{ color: '#41525d', fontWeight: '300', margin: '20px 0 10px 0' }}>WhatsApp Universo Mídia</h1>
                        <p style={{ color: '#667781', margin: 0 }}>Selecione uma conversa para começar.</p>

                        {!isMobile && (
                            <button 
                                onClick={() => setShowNewChatModal(true)} 
                                style={{ 
                                    marginTop: '40px', 
                                    padding: '14px 28px', 
                                    background: '#00a884', 
                                    color: '#fff', 
                                    borderRadius: '30px', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    fontSize: '16px', 
                                    fontWeight: 'bold', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '10px', 
                                    boxShadow: '0 4px 12px rgba(0,168,132,0.3)',
                                    transition: '0.2s transform ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <MessageSquare size={20} /> Iniciar Nova Conversa
                            </button>
                        )}
                    </div>
                )}
            </div>

            {rightPanelOpen && selectedChat && (
                <div className="wa-right-panel" style={rightPanelStyle}>
                    <div className="wa-right-header" style={{ padding: '15px 20px', background: '#fff', borderBottom: '1px solid #e1e9eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <h4 style={{ margin: 0, color: '#111b21', fontSize: '16px' }}>Detalhes do Cliente</h4>
                        <X size={20} color="#54656f" style={{ cursor: 'pointer' }} onClick={() => setRightPanelOpen(false)} />
                    </div>
                    <div className="wa-right-content custom-scrollbar" style={{ padding: '20px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>

                        <div 
                            onClick={() => { if (selectedChat.profilePicUrl && selectedChat.profilePicUrl !== 'no_pic') setPreviewImage(selectedChat.profilePicUrl); }}
                            style={{ 
                                width: '140px', height: '140px', borderRadius: '50%', marginBottom: '15px', 
                                overflow: 'hidden', backgroundColor: '#dfe5e7', display: 'flex', 
                                alignItems: 'center', justifyContent: 'center', fontSize: '50px', 
                                color: '#fff', cursor: selectedChat.profilePicUrl && selectedChat.profilePicUrl !== 'no_pic' ? 'pointer' : 'default',
                                flexShrink: 0 
                            }}
                        >
                            {selectedChat.profilePicUrl && selectedChat.profilePicUrl !== 'no_pic' ? (
                                <img src={selectedChat.profilePicUrl} alt="avatar" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                selectedChat.contactName?.charAt(0).toUpperCase() || <User size={60} color="#fff" />
                            )}
                        </div>

                        <h3 style={{ margin: '0 0 5px 0', color: '#111b21', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '18px', textAlign: 'center', wordBreak: 'break-word', width: '100%' }}>
                            {selectedChat.contactName}
                            {selectedChat.is_favorite && <Pin size={16} fill="#ffb02e" color="#ffb02e" style={{ flexShrink: 0 }}/>}
                        </h3>
                        <p style={{ margin: '0 0 20px 0', color: '#667781', fontSize: '14px', textAlign: 'center', wordBreak: 'break-word', width: '100%' }}>
                            {selectedChat.remote_jid?.startsWith('web_') ? 'Visitante Site' : cleanJid(selectedChat.remote_jid)}
                        </p>

                        <div style={{ width: '100%', background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'left', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', boxSizing: 'border-box', border: '1px solid #e1e9eb', flexShrink: 0 }}>
                            <div style={{ marginBottom: '12px' }}>
                                <small style={{ color: '#667781', display: 'block', fontSize: '12px', marginBottom: '2px' }}>WhatsApp / Telefone</small>
                                <span style={{ color: '#111b21', fontSize: '14px', fontWeight: '500', wordBreak: 'break-all', display: 'block' }}>{contactData.whatsapp || 'Não informado'}</span>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <small style={{ color: '#667781', display: 'block', fontSize: '12px', marginBottom: '2px' }}>E-mail</small>
                                <span style={{ color: '#111b21', fontSize: '14px', fontWeight: '500', wordBreak: 'break-all', display: 'block' }}>{contactData.email || 'Não informado'}</span>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <small style={{ color: '#667781', display: 'block', fontSize: '12px', marginBottom: '2px' }}>CPF</small>
                                <span style={{ color: '#111b21', fontSize: '14px', fontWeight: '500', display: 'block' }}>{contactData.cpf || 'Não informado'}</span>
                            </div>
                            <div>
                                <small style={{ color: '#667781', display: 'block', fontSize: '12px', marginBottom: '2px' }}>Cidade / UF</small>
                                <span style={{ color: '#111b21', fontSize: '14px', fontWeight: '500', display: 'block' }}>{contactData.cidade ? `${contactData.cidade}/${contactData.estado}` : 'Não informado'}</span>
                            </div>
                        </div>

                        <button className="wa-btn-contact" onClick={() => setShowAddContactModal(true)} style={{ marginTop: '20px', width: '100%', boxSizing: 'border-box', padding: '12px', background: '#00a884', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '600', fontSize: '15px', flexShrink: 0, transition: '0.2s' }}>
                            <Edit3 size={18} /> Editar Cadastro
                        </button>
                    </div>
                </div>
            )}

            {showTagManager && (
                <div className="wa-overlay" style={{ zIndex: 999999, backdropFilter: 'blur(4px)' }}>
                    <div className="wa-modal-premium" style={{ maxWidth: '550px', width: '90%', borderRadius: '16px', overflow: 'hidden' }}>

                        <div className="wa-modal-premium-header" style={{ background: '#fff', borderBottom: '1px solid #f1f5f9', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ background: '#e0f2fe', padding: '10px', borderRadius: '12px' }}>
                                    <Tag size={24} color="#0284c7" style={{ transform: 'rotate(90deg)' }} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, color: '#0f172a', fontSize: '18px', fontWeight: '700' }}>Etiquetas do Cliente</h3>
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Organize e destaque este contato com cores</span>
                                </div>
                            </div>
                            <X size={24} onClick={() => setShowTagManager(false)} style={{ cursor: 'pointer', color: '#94a3b8' }} />
                        </div>

                        <div className="wa-modal-premium-body custom-scrollbar" style={{ maxHeight: '380px', overflowY: 'auto', padding: '20px', background: '#f8fafc' }}>
                            <div style={{ marginBottom: '15px', background: '#fff', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '6px' }}>Motivo / Observação ao aplicar (opcional):</label>
                                <input 
                                    type="text" 
                                    placeholder="Ex: Cliente VIP, Aguardando Pagamento..." 
                                    value={motivoTag} 
                                    onChange={(e) => setMotivoTag(e.target.value)} 
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13.5px', color: '#1e293b' }}
                                />
                            </div>

                            {availableTags.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#64748b', padding: '30px 0' }}>
                                    <Tag size={40} color="#cbd5e1" style={{ marginBottom: '10px', transform: 'rotate(90deg)' }} />
                                    <p>Nenhuma etiqueta cadastrada no sistema.</p>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                                    {availableTags.map(tag => {
                                        const isSelected = selectedChat?.tags?.some(t => t.id === tag.id) || false;

                                        return editingTag === tag.id ? (
                                            <form key={tag.id} onSubmit={handleUpdateTag} style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: '#fff', border: '1px solid #00a884', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                                <input type="text" autoFocus value={editTagData.nome} onChange={e => setEditTagData({ ...editTagData, nome: e.target.value })} placeholder="Nome..." style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%', outline: 'none' }} />
                                                <textarea rows="2" value={editTagData.descricao} onChange={e => setEditTagData({ ...editTagData, descricao: e.target.value })} placeholder="Descrição interna (opcional)..." style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '100%', outline: 'none', resize: 'none', fontSize: '12px' }} />
                                                <button type="submit" style={{ background: '#00a884', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', width: '100%' }}><CheckCheck size={16} style={{ margin: 'auto' }} /></button>
                                            </form>
                                        ) : (
                                            <div key={tag.id} style={{
                                                display: 'flex', flexDirection: 'column', gap: '8px',
                                                padding: '12px 16px', borderRadius: '12px',
                                                border: `2px solid ${isSelected ? tag.cor : '#e2e8f0'}`,
                                                background: isSelected ? `${tag.cor}10` : '#fff',
                                                cursor: 'pointer', transition: 'all 0.2s ease',
                                                boxShadow: isSelected ? `0 4px 12px ${tag.cor}25` : '0 2px 4px rgba(0,0,0,0.02)'
                                            }} onClick={() => handleToggleTag(tag)}>

                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                                                        <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: tag.cor, border: '2px solid #fff', outline: `2px solid ${tag.cor}60`, flexShrink: 0 }}></div>
                                                        <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: isSelected ? '700' : '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tag.nome}</span>
                                                    </div>
                                                    {isSelected && <CheckCircle size={18} color={tag.cor} />}
                                                </div>

                                                {tag.descricao && (
                                                    <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.3' }}>
                                                        {tag.descricao}
                                                    </div>
                                                )}

                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '4px' }}>
                                                    <button onClick={(e) => { e.stopPropagation(); startEditing(tag); }} style={{ background: 'rgba(0,0,0,0.04)', border: 'none', borderRadius: '6px', padding: '4px', color: '#64748b', cursor: 'pointer', display: 'flex' }} title="Editar Etiqueta no Sistema"><Edit3 size={14} /></button>
                                                    <button onClick={(e) => { e.stopPropagation(); handleDeleteTagSystem(tag.id); }} style={{ background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: '6px', padding: '4px', color: '#ef4444', cursor: 'pointer', display: 'flex' }} title="Excluir do Sistema"><Trash2 size={14} /></button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div style={{ padding: '20px', background: '#fff', borderTop: '1px solid #e2e8f0' }}>
                            <form onSubmit={handleCreateTag} style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <input type="color" value={newTagData.cor} onChange={(e) => setNewTagData({...newTagData, cor: e.target.value})} style={{ width: '40px', height: '40px', padding: '0', border: 'none', borderRadius: '8px', cursor: 'pointer', background: 'transparent' }} title="Escolher Cor" />
                                    <input type="text" placeholder="Nome da nova etiqueta..." value={newTagData.nome} onChange={(e) => setNewTagData({...newTagData, nome: e.target.value})} style={{ flex: 1, padding: '10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', fontSize: '14px', color: '#0f172a' }} required />
                                </div>
                                <textarea rows="2" placeholder="Descrição interna (Opcional)..." value={newTagData.descricao} onChange={(e) => setNewTagData({...newTagData, descricao: e.target.value})} style={{ width: '100%', padding: '10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', fontSize: '13px', color: '#0f172a', resize: 'none', fontFamily: 'inherit' }} />
                                <button type="submit" style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '600' }}>
                                    <Plus size={18} /> Cadastrar Nova Etiqueta
                                </button>
                            </form>
                        </div>

                    </div>
                </div>
            )}

            {contextMenu.show && (
                <div 
                    className="wa-floating-menu" 
                    style={{ 
                        position: 'fixed', 
                        top: contextMenu.y, 
                        left: contextMenu.x, 
                        zIndex: 999999, 
                        background: '#fff', 
                        boxShadow: '0 4px 16px rgba(0,0,0,0.2)', 
                        borderRadius: '12px', 
                        padding: '6px 0', 
                        minWidth: '200px', 
                        border: '1px solid #e1e9eb' 
                    }} 
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="wa-floating-item" onClick={() => { handleToggleFavorite(contextMenu.chat); }}>
                        {contextMenu.chat.is_favorite ? <><PinOff size={16} color="#667781" /> Remover dos Favoritos</> : <><Pin size={16} fill="#ffb02e" color="#ffb02e" /> Fixar / Favoritar</>}
                    </div>
                    <div className="wa-floating-item" onClick={() => { carregarHistorico(contextMenu.chat); setContextMenu({ ...contextMenu, show: false }); }}>
                        <MessageSquare size={16} /> Abrir Conversa
                    </div>
                    <div className="wa-floating-item" onClick={() => { handleMarkAsRead(contextMenu.chat); }}>
                        <CheckCheck size={16} /> Marcar como lida
                    </div>
                    <div style={{ borderTop: '1px solid #f0f2f5', margin: '4px 0' }}></div>
                    <div className="wa-floating-item danger" onClick={() => { handleDeleteChat(contextMenu.chat); setContextMenu({ ...contextMenu, show: false }); }}>
                        <Trash2 size={16} /> Excluir Conversa
                    </div>
                </div>
            )}

            {activeMessageMenu && (
                <div 
                    className="wa-msg-dropdown-menu" 
                    onClick={(e) => e.stopPropagation()} 
                    style={{ position: 'fixed', top: `${activeMessageMenu.y}px`, left: `${activeMessageMenu.x}px`, background: '#fff', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', width: '160px', zIndex: 99999, padding: '6px 0', border: '1px solid #e1e9eb' }}
                >
                    <div 
                        onClick={() => { 
                            const msg = activeMessageMenu.msg;
                            if (msg) setReplyingTo(msg);
                            setActiveMessageMenu(null); 
                            textareaRef.current?.focus(); 
                        }}
                        style={{ padding: '8px 14px', fontSize: '13px', color: '#111b21', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f6f6'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                        <CornerUpLeft size={14} color="#54656f" /> Responder
                    </div>
                    <div 
                        onClick={() => { 
                            const msg = activeMessageMenu.msg;
                            const textContent = msg?.text || msg?.content || "";
                            navigator.clipboard.writeText(textContent); 
                            toast.success("Texto copiado!"); 
                            setActiveMessageMenu(null); 
                        }}
                        style={{ padding: '8px 14px', fontSize: '13px', color: '#111b21', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f6f6'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                        <Copy size={14} color="#54656f" /> Copiar
                    </div>
                    <div style={{ borderTop: '1px solid #f0f2f5', margin: '4px 0' }}></div>
                    <div style={{ padding: '6px 12px', display: 'flex', justifyContent: 'space-between', gap: '2px' }}>
                        {['👍', '❤️', '😂', '😮', '🙏'].map(emoji => (
                            <span
                                key={emoji}
                                onClick={() =>
                                    handleReact(
                                        activeMessageMenu.msg,
                                        emoji
                                    )
                                }
                                style={{
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '2px'
                                }}
                            >
                                {emoji}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {fileToUpload && (
                <div className="wa-file-upload-overlay">
                    <div className="wa-file-upload-modal">
                        <div className="wa-file-upload-body">
                            {fileToUpload.type.startsWith('image/') && filePreviewUrl ? (
                                <img src={filePreviewUrl} className="wa-upload-preview-img" alt="preview" style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} />
                            ) : fileToUpload.type.startsWith('video/') && filePreviewUrl ? (
                                <video src={filePreviewUrl} controls style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }} />
                            ) : (
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                    <FileText size={80} color="#667781" />
                                    <span style={{marginTop: '10px', fontSize: '14px', color: '#111b21'}}>{fileToUpload.name}</span>
                                </div>
                            )}
                        </div>
                        <div className="wa-file-upload-footer">
                            <input value={fileCaption} onChange={(e) => setFileCaption(e.target.value)} placeholder="Legenda..." />
                            <button type="button" onClick={handleSend}><Send size={24} /></button>
                        </div>
                    </div>
                </div>
            )}

            {previewImage && (
                <div className="wa-preview-overlay" onClick={() => setPreviewImage(null)}>
                    <div className="wa-preview-content" onClick={(e) => e.stopPropagation()}>
                        <button className="wa-preview-close" onClick={() => setPreviewImage(null)}><X size={32} color="#fff" /></button>

                        {isImageUrl(previewImage) ? (
                            <img src={previewImage} alt="Preview" />
                        ) : previewImage.match(/\.(mp4|mov|avi)$/i) || previewImage.includes('video') ? (
                            <video controls autoPlay style={{ maxWidth: '90vw', maxHeight: '80vh' }}><source src={previewImage} type="video/mp4" /></video>
                        ) : previewImage.match(/\.pdf($|\?)/i) || previewImage.includes('pdf') ? (
                            <embed src={previewImage} type="application/pdf" style={{ width: '80vw', height: '90vh', borderRadius: '8px' }} />
                        ) : (
                            <iframe src={previewImage} title="Document Preview" style={{ width: '80vw', height: '90vh', border: 'none', background: '#fff', borderRadius: '8px' }} />
                        )}
                    </div>
                </div>
            )}

            {confirmModal.show && (
                <div className="wa-overlay" style={{ zIndex: 999999 }}>
                    <div className="wa-modal-premium" style={{ maxWidth: '400px', margin: 'auto', marginTop: '15vh' }}>
                        <div className="wa-modal-premium-header">
                            <h3>{confirmModal.title}</h3>
                            <X size={24} onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })} style={{ cursor: 'pointer' }} />
                        </div>
                        <div className="wa-modal-premium-body" style={{ padding: '20px', textAlign: 'center', fontSize: '16px' }}>
                            <p>{confirmModal.message}</p>
                        </div>
                        <div className="wa-modal-footer-premium" style={{ justifyContent: 'center', gap: '15px' }}>
                            <button type="button" className="wa-btn-cancel" onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}>Cancelar</button>
                            <button type="button" className="wa-btn-save" style={{ background: '#ff3b30' }} onClick={async () => {
                                if (confirmModal.onConfirm) await confirmModal.onConfirm();
                                setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
                            }}>
                                Confirmar Exclusão
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAddContactModal && (
                <div className="wa-overlay" style={{ zIndex: 999999 }}>
                    <div className="wa-modal-premium" style={{ maxWidth: '500px', width: '90%' }}>
                        <div className="wa-modal-premium-header">
                            <h3>{contactData.id ? 'Editar Cadastro' : 'Novo Contato'}</h3>
                            <X size={24} onClick={() => setShowAddContactModal(false)} style={{ cursor: 'pointer' }} />
                        </div>
                        <form onSubmit={handleContactSubmit} className="wa-modal-premium-body custom-scrollbar" style={{ maxHeight: '65vh', overflowY: 'auto', padding: '20px' }}>
                            <div style={{ display: 'grid', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#667781', marginBottom: '4px', fontWeight: 'bold' }}>Nome</label>
                                    <input type="text" value={contactData.nome} onChange={e => setContactData({...contactData, nome: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }} required />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#667781', marginBottom: '4px', fontWeight: 'bold' }}>WhatsApp</label>
                                    <input type="text" value={contactData.whatsapp} onChange={e => setContactData({...contactData, whatsapp: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#667781', marginBottom: '4px', fontWeight: 'bold' }}>E-mail</label>
                                    <input type="email" value={contactData.email} onChange={e => setContactData({...contactData, email: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#667781', marginBottom: '4px', fontWeight: 'bold' }}>CPF</label>
                                    <input type="text" value={contactData.cpf} onChange={e => setContactData({...contactData, cpf: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#667781', marginBottom: '4px', fontWeight: 'bold' }}>CEP</label>
                                    <input type="text" value={contactData.cep} onChange={handleCepChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }} placeholder="00000-000" />
                                    {loadingCep && <small style={{ color: '#00a884', marginTop: '4px', display: 'block' }}>Buscando endereço...</small>}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', color: '#667781', marginBottom: '4px', fontWeight: 'bold' }}>Endereço</label>
                                        <input type="text" value={contactData.endereco} onChange={e => setContactData({...contactData, endereco: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', color: '#667781', marginBottom: '4px', fontWeight: 'bold' }}>Cidade</label>
                                        <input type="text" value={contactData.cidade} onChange={e => setContactData({...contactData, cidade: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', color: '#667781', marginBottom: '4px', fontWeight: 'bold' }}>UF</label>
                                        <input type="text" value={contactData.estado} onChange={e => setContactData({...contactData, estado: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box' }} maxLength="2" />
                                    </div>
                                </div>
                            </div>
                        </form>
                        <div className="wa-modal-footer-premium" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px 20px', borderTop: '1px solid #f1f5f9' }}>
                            <button type="button" className="wa-btn-cancel" onClick={() => setShowAddContactModal(false)} style={{ padding: '10px 15px', border: 'none', background: '#f1f5f9', color: '#64748b', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Cancelar</button>
                            <button type="button" className="wa-btn-save" onClick={handleContactSubmit} style={{ padding: '10px 15px', border: 'none', background: '#00a884', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Salvar Dados</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default WhatsAppChat;